/**
 * Tenant RBAC role registry — single source of truth for the 10 tenant
 * roles (7 operational, including the permission-inert `clockin_only`, + 3
 * accountant external-access), their permission lists, and the role-power
 * partial order. The count follows this object; the generated reconcile SQL's
 * `-- Roles: N (...)` header line is the cheapest place to re-read it.
 *
 * Per permissions.md §2.1 + ADR-0029.
 *
 * Per-tenant role seeding reads directly from ROLE_REGISTRY at runtime via
 * `seedDefaultRolesForTenant` (modules/tenants/domain/seed-default-roles.ts),
 * called from the provisioning saga. The global SQL seed pipeline and
 * `scripts/generate-tenant-seed.ts` are retired (Task 2.8).
 * The matrix runner reads roles from here. CI gate rbac-no-non-owner-wildcards
 * greps this file for non-Owner wildcards.
 *
 * Adding a new permission to an existing role:
 *   1. add the string to the appropriate permissions array below;
 *   2. run `pnpm gen:tenant-roles-reconcile`;
 *   3. ensure the corresponding handler is decorated with the same string.
 *
 * There is NO manual step for docs/permissions-matrix.md. It is not committed:
 * the deploy-staging `permissions-matrix` job regenerates it on main and uploads
 * it as an artifact, and PR CI asserts generator validity (0 undecorated
 * routes) rather than committed bytes. `scripts/generate-permissions-matrix.cjs`
 * -- named as a step here until #12380 -- does not exist; the generator lives in
 * tests/rbac-matrix/generate-md.ts and tests/rbac-matrix/run.ts. If you want an
 * immediate local refresh anyway, the sanctioned entry point is
 * `pnpm regen:route-artifacts --regenerate-matrix`.
 *
 * Step 2 is the one that is easy to miss and costly to miss (#12380). Built-in
 * role rows are a PER-TENANT SNAPSHOT of this file, materialized by the
 * generated bootstrap-58-tenant-roles-registry-reconcile.sql (under
 * apps/migrations-runner/sql/). The filename is written unbroken on purpose --
 * a path hyphen-wrapped across two comment lines is not greppable and does not
 * survive a copy-paste, which is the same "reader cannot find the second site"
 * failure this comment exists to end.
 *
 * The two reads of this constant are at DIFFERENT times, which is the whole
 * trap and squares the paragraph above with `seedDefaultRolesForTenant` above
 * that:
 *   * PROVISION time -- seedDefaultRolesForTenant reads ROLE_REGISTRY live and
 *     writes the snapshot, so any tenant signed up after your edit is correct
 *     with or without step 2;
 *   * REQUEST time -- effective permissions resolve from the stored snapshot
 *     (modules/tenants/repositories/effective-permissions.ts), never from this
 *     constant, so every tenant that already existed is frozen at whatever the
 *     registry said on ITS provision day until the reconcile runs.
 *
 * Skip step 2 and the permission exists in the type system and is enforced by
 * routes while reaching no EXISTING tenant — the role picker looks correct, the
 * API returns 403, a fresh local test tenant behaves perfectly, and nothing
 * about that symptom points back here. `Gate — tenant-roles reconcile SQL fresh
 * (inline)` fails in `pnpm prepush` when you forget; this list exists because
 * it did not, and three separate review passes read THIS comment looking for
 * exactly that step and concluded there was no second site.
 *
 * Tests that mirror a role's list will also fail until updated
 * (assertMirrorsRegistryRole in tests/unit/tenants-invite-routes.test.ts and
 * tests/rbac-fixtures/03-invite-manager-invites-owner.test.ts). That is the
 * mirror drifting, not this edit being wrong.
 *
 * Adding a new role is V2: requires a successor ADR.
 */
/**
 * The 10 tenant roles (7 operational + 3 accountant external-access). Permission lists are explicit (no module.* wildcards
 * on non-Owner roles per R-104). Owner gets ['*'] which matches everything.
 *
 * ── Code-intelligence permissions (assistant.code.*) ─────────────────────────
 * `assistant.code.ground` — granted to all tenant-facing roles (PR-5 non-staff
 *   mode). PR-6 eval harness gates passed: rate-limit fence, secret-scrub
 *   coverage, and schema-isolation gate all green. Allows grounded code-intel
 *   answers for any assistant-enabled employee.
 *
 * `assistant.code.trace` — platform/support staff ONLY (render-gated,
 *   requiresStepUp + requiresAudit). No tenant-facing role (employee, manager,
 *   owner, supervisor, cashier, server, accountant variants) may hold this
 *   permission. Added to platform_support role when that role is defined.
 * ─────────────────────────────────────────────────────────────────────────────
 */
export const ROLE_REGISTRY = {
    owner: {
        id: 'owner',
        displayName: 'Owner',
        description: 'Founder or GM with full authority. Owner gets * — matches everything in their tenant scope.',
        // Note: settings.modules.payments.{read,write} and settings.modules.quotes.{read,write}
        // are NOT granted to any other built-in role below — Owner's '*' wildcard covers
        // them by default. Quote settings metadata permits explicit custom-role delegation;
        // it does not change this built-in-role policy (see quotes/settings/permissions.ts).
        permissions: ['*'],
        isOwner: true,
        isAssignable: true,
    },
    manager: {
        id: 'manager',
        displayName: 'Manager',
        description: 'Store manager or restaurant manager. Most operations except finance + Owner-only actions.',
        permissions: [
            'payment-hub.settings.read',
            'payment-hub.settings.write',
            'settings.pages.payment-page.read',
            'settings.pages.payment-page.write',
            'payment-hub.reasons.write',
            'payment-hub.custom-fields.write',
            'payment-hub.requests.read',
            'payment-hub.requests.create',
            'payment-hub.requests.revoke',
            'payment-hub.requests.retry-settlement',
            'payment-hub.requests.resend-receipt',
            'payment-hub.refund',
            'payment-hub.dispute.write',
            'payment-hub.requests.set-business-date',
            'payment-hub.requests.resolve-refund',
            'commerce.order.create',
            'commerce.order.read',
            'commerce.order.update',
            'commerce.order.cancel',
            'commerce.order.refund',
            'commerce.order.tax-exempt-recovery',
            'commerce.order.void',
            'payments.capture.read',
            'payments.refund',
            'payments.refund.requires-pin',
            'payments.tender.read',
            'payments.tender.refund',
            'payments.tender.void',
            'payments.tender.tip.adjust',
            'payments.vault.store',
            'payments.vault.read',
            'payments.vault.revoke',
            'payments.orphan.read',
            'payments.orphan.resolve',
            'payments.quick-charge-linkage.read',
            'payments.quick-charge-linkage.resolve',
            'payments.surcharge-reversal.read',
            'payments.surcharge-reversal.replay',
            'payments.surcharge-drift.read',
            'payments.cnp-refund-unresolved.read',
            'catalog.receiving.create',
            'catalog.receiving.post',
            'catalog.adjustment.create',
            'catalog.adjustment.post',
            'workforce.shift.create',
            'workforce.shift.close',
            'workforce.shift.transfer',
            'workforce.payroll.read',
            'workforce.payroll.write',
            'workforce.payroll.admin',
            'financials.dashboard.view',
            'financials.report.read',
            'financials.report-view.share',
            'financials.journal-entries.read',
            // MP-1b PR #969/#970 detector queue surfaces; resolve action gated to manager per audit-log weight (PR #971/#972).
            'financials.findings.read',
            'financials.findings.resolve',
            'financials.deposit.read',
            'financials.deposit.deposit',
            'financials.deposit.void',
            'financials.customer-deposits.apply',
            // Financials reads + GL DLQ ops. Chart create/seed stay Owner-only via
            // OWNER_ONLY_ALLOWLIST (rare administrative writes).
            'financials.chart.read',
            // GL mappings dashboard (COA PR6 / ADR-0093). Manager reads coverage +
            // the unmapped queues and assigns mappings; the write path is audited.
            'financials.mappings.read',
            'financials.mappings.write',
            // Vertical COA overlay opt-in surface (ADR-0080 Track D). Single manage
            // perm gates both the read (available/enabled list) and the
            // enable/disable mutations — the operator settings page reads + writes
            // under one slug. Owner picks up via '*'.
            'financials.coa-overlays.manage',
            'financials.gl-dlq.read',
            'financials.cash-basis-dlq.read',
            'financials.cash-basis-dlq.replay',
            'financials.gl-dlq.resolve',
            'financials.gl-dlq.replay',
            // Statement reads (Epic 2 closure B4): Trial Balance + account drill-down;
            // future B5 P&L + B6 Balance Sheet share this slug. Audited per A0
            // taxonomy because the rows expose financial figures (vs the benign
            // period.read which only shows boundaries/status).
            'financials.statements.read',
            // #8617: narrow customer-deposit lookup, granted more broadly than the
            // GL-exposing financials.statements.read (see the cashier/server block
            // below for the POS-facing grant).
            'financials.customer-deposits.lookup',
            // Period boundaries/status read (Epic 2 closure D2). The lightweight
            // counterpart to period.preflight.read — lets the statement-page period
            // selectors (TB/P&L/BS/Cash) list periods without the heavier preflight
            // perm. Benign: exposes period boundaries + status only, no GL figures.
            'financials.period.read',
            // Cash Activity report (Epic 2 closure B7, ADR-0070). Same audit posture
            // as financials.statements.read — exposes GL figures bucketed by source
            // event type.
            'financials.cash-activity.read',
            // GL Detail report and GAAP Statement of Cash Flows (Financial Reports V1).
            // Same audit posture as financials.statements.read. Manager-level by default.
            'financials.gl-detail.read',
            'financials.gl-code-summary.read',
            'financials.cash-flows.read',
            // #9120 follow-up: per-location drill-down on the report reads above.
            // Granted to exactly the four roles that hold financials.statements.read
            // (manager + the three accountant variants) because that is the set the
            // report handler ALREADY served. parsedReportQuery hardcoded
            // `userPermissions: ['financials.dimension.location.read']` with the note
            // that the route gate plus allowed_location_ids were the real controls, so
            // this permission was inert: every statements-reader got location scope no
            // matter what the registry said, and revoking it via a custom role changed
            // nothing. The handler now reads the real grant, which is why the list here
            // has to name that same four-role set -- a manager-only grant would have
            // silently REMOVED drill-down from the three accountant roles.
            // The scoping control is unchanged and independent: resolveFinancialReportScope
            // still checks tenant_users.allowed_location_ids after the permission and
            // throws LOCATION_NOT_AUTHORIZED for a location outside the holder's set,
            // so this grant cannot widen a location-restricted holder's reach.
            // financials.classes.read stays Owner-only by contrast -- see
            // scripts/lib/owner-only-permissions.cjs for that decision's rationale.
            'financials.dimension.location.read',
            // #9120: the .export siblings of the three reads above (cash-activity,
            // gl-detail, cash-flows). Every OTHER financials report family this role
            // holds -- statements, ap-reports, payroll-reports, fixed-assets-reports,
            // operational-reports, reconciliation-reports -- already pairs .read with
            // .export. These three were orphaned identically across all four
            // report-holding roles (manager + the three accountant variants), which
            // reads as an oversight rather than a deliberate "you may export 6 of the
            // 9 reports you can read" policy. Exporting data the role already renders
            // on screen is a format change, not a privilege escalation; same
            // egress-audit posture as the exports immediately below.
            'financials.cash-activity.export',
            'financials.gl-detail.export',
            'financials.cash-flows.export',
            // Statement CSV exports (Epic 2 closure B8). Egress action — audited
            // per A0 taxonomy. Manager-level by default; tighter scoping (e.g.,
            // Owner-only) is a policy call deferred until UI ships.
            'financials.statements.export',
            // AP, payroll, fixed-assets, and operational report exports. Same egress-audit
            // posture as financials.statements.export.
            'financials.ap-reports.export',
            'financials.payroll-reports.export',
            'financials.fixed-assets-reports.export',
            'financials.operational-reports.export',
            // Manual journals (Epic 2 closure C2, ADR-0071). Operator-authored
            // adjusting entries. A0 registered the canonical posture (financial_critical
            // step-up on post + reverse; audit on every slug). Owner picks up via '*'.
            'financials.manual-journal-drafts.write',
            'financials.manual-journal-drafts.delete',
            'financials.manual-journals.read',
            'financials.recognition.read',
            'financials.manual-journals.approve',
            'financials.manual-journals.post',
            'financials.recognition.run',
            'financials.recognition.recover',
            'financials.manual-journals.reverse',
            // GL mapping gap drain approval (Tier 3 SoD second-eye).
            'financials.gl-mapping.approve',
            // COA PR8: manager can read the CoA change-log timeline + recurring templates.
            'financials.chart.change-log.read',
            'financials.recurring-templates.read',
            // Reconciliation workflow (Phase 1 plan 2026-06-04). undo is owner-only.
            'financials.reconciliation.read',
            'financials.reconciliation.start',
            'financials.reconciliation.clear',
            'financials.reconciliation.finalize',
            'financials.reconciliation.adjust',
            'financials.reconciliation.amend',
            'financials.reconciliation.import',
            'financials.reconciliation.rule.manage',
            // Billing Deposits & Reconciliation surface (Plan D, D3 ruling) --
            // deposit-aging obligation view + named GL breaks, scoped separately
            // from the broader financials.reconciliation.* set above (least
            // privilege: viewing this billing-specific surface should not imply
            // blanket GL-DLQ triage powers across unrelated financials areas).
            'customer-billing.reconciliation.read',
            'customer-billing.reconciliation.resolve',
            // CNP invoice keyed-card charging (2026-08-31 design doc) -- distinct
            // from pos.virtual-terminal.access so AR/billing staff get this
            // without POS virtual-terminal operators automatically inheriting a
            // new AR money-movement power, and vice versa.
            'customer-billing.invoice.charge-card',
            // Hosted pay page tips configuration (V1.5 #7, #8506).
            'customer-billing.pay-page-settings.read',
            'customer-billing.pay-page-settings.write',
            // Customer-profile "set up a billing plan" entry point (plan +
            // subscription in one call) -- same manager tier as the two grants
            // immediately above.
            'customer-billing.subscriptions.create',
            // Billing workspace — Receivables/Recurring/Attention (PR-2A, CONTRACT §7.1).
            // Manager gets the full tier, mirroring its existing full-write posture
            // on customer-billing.subscriptions.create just above. plan.billing-control
            // is granted here BEHIND step-up (§7.2) — the step-up requirement is the
            // safety control, not withholding the grant.
            'customer-billing.workspace.read',
            'customer-billing.subscription.read',
            'customer-billing.subscription.create',
            'customer-billing.subscription.modify',
            'customer-billing.subscription.cancel',
            'customer-billing.plan.read',
            'customer-billing.plan.write',
            'customer-billing.plan.archive',
            'customer-billing.plan.billing-control',
            'customer-billing.charge.retry',
            'customer-billing.pay-link.create',
            'customer-billing.pay-link.read',
            'customer-billing.pay-link.revoke',
            // Customer-billing DLQ reader/redrive (Bundle C, follow-up to #7410).
            // Manager-only, mirroring financials.gl-dlq.read/.resolve/.replay's own
            // Manager-only placement -- redrive re-attempts a money-adjacent AR
            // settle, same tier as the other DLQ-triage permissions above.
            'customer-billing.dlq.read',
            'customer-billing.dlq.redrive',
            // Reconciliation exception reports (Financial Reports V2 Phase 2):
            // settlement gaps, ACH returns, chargebacks, undeposited funds, GL DLQ.
            // Same audit posture as financials.statements.read; export mirrors
            // financials.statements.export.
            'financials.reconciliation-reports.read',
            'financials.reconciliation-reports.export',
            // AR aging reports (Financial Reports V2 Phase 3): subledger-canonical
            // open-balance aging (detail + summary) with GL control tie-out. Same
            // audit posture as financials.statements.read.
            'financials.ar-reports.read',
            // AP aging + vendor payment history reports (Financial Reports V2 Phase 4).
            // Payroll summary reports. Fixed-assets depreciation schedule reports.
            // Operational reports (sales, payment methods, voids/refunds, tips, tax).
            'financials.ap-reports.read',
            'financials.payroll-reports.read',
            'financials.fixed-assets-reports.read',
            'financials.operational-reports.read',
            // Settlement evidence is read-only; batch mutations remain Owner-only by default.
            'payments.settlements.read',
            // IRS 1099 e-filing (V1, Slice B). Manager can view compile-readiness
            // and fix gap-filler data (address/TIN-type/consent); filing/submission
            // actions (Slice C/D) are owner-only via '*'.
            'tax-1099-filing.records.read',
            'tax-1099-filing.payees.write',
            // Fixed assets (V1). Read + write available at manager level; GL-posting
            // operations (compute/post/reverse) are owner-only via '*'.
            'fixed-assets.assets.read',
            'fixed-assets.assets.write',
            // Bulk CSV import of legacy fixed assets (B7). Manager-level, same as its
            // peer fixed-assets.assets.write and the other Manager bulk imports
            // (customers.bulk-import, catalog.items.bulk-import): it creates assets and
            // seeds opening accumulated depreciation via a synthetic depreciation run
            // that deliberately never posts to the GL, so it is not a money-touching
            // GL operation and carries no step-up (requiresAudit only). Owner via '*'.
            'fixed-assets.assets.bulk-import',
            'fixed-assets.categories.read',
            'fixed-assets.depreciation-runs.read',
            // Budgets (V1). Manager gets read, write, submit, import, export.
            // approve/lock/archive are owner-only (covered by '*').
            'budgets.plans.read',
            'budgets.plans.write',
            'budgets.versions.submit',
            'budgets.import',
            'budgets.export',
            'tenants.invite.create',
            'tenants.role-assignments.read',
            'tenants.role-assignments.grant',
            'tenants.role-assignments.revoke',
            // Role catalog visibility (role-manager rework PR-3 per ADR-0061).
            // Manager can see the role list to make assignment decisions, but
            // CANNOT mutate the role catalog itself — `tenants.roles.manage`
            // is Owner-only (see owner's '*' wildcard which covers it).
            'tenants.roles.read',
            // User-management surface (PR-1 of user-management plan, 2026-05-28).
            // Manager gets read + manage + security.manage. lifecycle.manage is
            // owner-only (cross-tenant identity status); covered by Owner '*'.
            'tenants.users.read',
            'tenants.users.manage',
            'tenants.users.security.manage',
            // Customers — granular vocabulary per docs/business-rules/customers-module.md.
            'customers.view',
            'customers.manage',
            'customers.merge',
            'customers.tags.manage',
            'customers.payment-methods.view',
            'customers.payment-methods.add',
            'customers.payment-methods.set-default',
            'customers.payment-methods.verify-ach',
            'customers.payment-methods.archive',
            // stored-method-charge-callers Phase 1: backoffice "charge card on
            // file" action. Manager+ only (NOT extended to supervisor/cashier
            // despite them holding view/add/set-default above) — mirrors the
            // verify-ach/archive administrative tier in THIS SAME family, one
            // rung more restrictive than add/set-default, per the established
            // void/refund pattern (see supervisor's and cashier's own comments
            // below: "verify-ach + archive stay Manager+ (administrative)").
            // A real-money charge is at least as sensitive as those two, and the
            // closest analogous money-placing permission in this registry
            // (payments.tender.capture) is scoped even narrower — cashier-only,
            // not manager — because it's a routine front-of-house action; this
            // one-off backoffice charge is the opposite shape (deliberate,
            // ad-hoc, Customer-360-initiated), so Manager+ is the right floor,
            // not cashier.
            'customers.payment-methods.charge',
            // Customer profiles V2 first-slice (sub-PR 8f); spec §4.6.
            'customers.view.merged',
            // The Customer 360 page opens on customers.view; the retired
            // 'customers.360page.access' flag was removed (launch-readiness plan, Slice 2 D3).
            'customers.contact.read',
            'customers.contact.write',
            'customers.financial.read',
            'customers.stored-value.read',
            'customers.membership.read',
            'customers.activity.read',
            'customers.tags.read',
            'customers.tags.write',
            'customers.relationships.read',
            'customers.documents.read',
            'customers.documents.write',
            'customers.notes.read',
            'customers.notes.write',
            'customers.notes.admin',
            'customers.bulk-import',
            'customers.smart-tags.read',
            'customers.smart-tags.write',
            // Bulk-action APIs (WS-0): preview a frozen audience + export it. Same
            // privilege tier as the other Manager bulk customer operations above.
            'customers.bulk-actions.preview',
            'customers.bulk-actions.export',
            'customers.ar-aging.read',
            'customers.manage-contacts',
            'customers.manage-tax',
            'house-accounts.view-credit',
            'reports.read',
            'reports.run',
            'reports.commerce.read',
            'reports.commerce.export',
            // Billing Reports tab (Reports foundation task 1) — same tier as
            // reports.commerce.read above; grants the general reports-viewer
            // role read access to the billing subscription/invoice/dunning reports.
            'reports.billing.read',
            // F&B reporting surface (S12 Phase-1, ADR-0139) — daypart/hourly/
            // menu-mix/discount-comp read-models. Read-only for now; no
            // .export counterpart since fnb-reporting has no export pipeline
            // wired yet (unlike reports.commerce.export above).
            'reports.fnb-reporting.read',
            // Dashboard landing surface (dashboard-v2 PR1) — operator landing page;
            // revenue tile is independently gated by 'commerce.revenue.view' on the
            // client (modules/dashboard/server/routes.ts and apps/backoffice/src/routes/_auth.$tenant.dashboard.tsx).
            'commerce.dashboard.view',
            'catalog.items.read',
            'catalog.items.create',
            'catalog.items.update',
            'catalog.items.delete',
            // PR-1b: CSV bulk import of catalog items + variants. Bulk write — audited.
            // Owner inherits via the '*' wildcard.
            'catalog.items.bulk-import',
            'catalog.modifiers.read',
            'catalog.modifiers.create',
            'catalog.modifiers.update',
            'catalog.modifier-groups.read',
            'catalog.modifier-groups.create',
            'catalog.modifier-groups.update',
            'catalog.modifier-groups.archive',
            'catalog.tax.read',
            'catalog.pricing.update',
            'tabs.transfer',
            'tabs.close',
            'gratuity.modify',
            'tenants.settings.read',
            'tenants.settings.update',
            // Identity audit log (V2 auth PR-Post Phase B) — admin-only PII surface.
            'tenants.audit.read',
            // Settings spine (S-Boot-26 PR-8b cluster) — per conv-settings-spine.md §1 Convention 7.
            // Module-scoped (covers both tenant + branding areas).
            'settings.modules.tenants.read',
            'settings.modules.tenants.write',
            'settings.modules.tenants.write.location',
            // Comms settings spine (#5477 burn-down): NOT mirrored from the tenants
            // trio above (unlike Document Templates alongside it) -- unlike tenants.read
            // (branding/timezone, benign), settings.modules.comms.read ALSO gates the
            // tenant-wide Recipient Book (/me/recipients, modules/comms/server/
            // recipient-book-routes.ts) -- real team-member email addresses. See
            // modules/quotes/server/routes.ts's own comment on that same permission:
            // it deliberately did NOT gate a quote-composer route on the broader
            // quotes.quote.read specifically to avoid widening visibility into that
            // recipient book. Granting comms.read/write/write.location to every role
            // (Supervisor/Cashier/Server) would reopen exactly that boundary for no
            // stated need -- none of the #5477 baseline routes are on their nav paths.
            // Manager only (matches this taxonomy's own typicalRoles: ['Owner', 'Manager']
            // in permission-groups.ts's comms-settings subgroup).
            'settings.modules.comms.read',
            'settings.modules.comms.write',
            'settings.modules.comms.write.location',
            // Document Templates settings (ADR-0174) -- same tenant-admin posture as
            // the tenants read/write pair immediately above. No write.location
            // variant (template management is tenant-wide only).
            'settings.modules.document-templates.read',
            'settings.modules.document-templates.write',
            // Day-2 go-live action (ADR-0111 PR-5). Manager can mark the tenant live once
            // all onboarding prerequisites are complete. Owner inherits via '*'.
            'settings.go-live.write',
            // Skip optional onboarding steps (skip_policy=allowed). Blocked steps remain blocked.
            'settings.onboarding.skip-step',
            // Manually complete an external-detection onboarding step (ADR-0111 PR-A).
            // Same posture as skip-step: Manager can mark eligible external steps done; Owner inherits via '*'.
            'settings.onboarding.mark-done',
            // Reopen a done/skipped onboarding step back to available (skip_policy!=blocked
            // only). Same posture as skip-step/mark-done: Manager can reopen; Owner inherits via '*'.
            'settings.onboarding.reopen-step',
            'settings.modules.kds.read',
            'settings.modules.kds.write',
            'settings.modules.kds.write.location',
            // Financials settings — deposit sub-account category configuration (deferred-revenue-s4).
            // Manager can read and write tenant-wide GL sub-account settings (same posture as
            // settings.modules.tenants.write above). No write.location variant — sub-accounts are
            // tenant-wide (no per-location override scope).
            'settings.modules.financials.read',
            'settings.modules.financials.write',
            // Tax settings spine (MEGA-2 Batch 3) — per conv-settings-spine.md §1
            // Convention 7. Gates GET/PATCH /me/settings/group and
            // /me/settings/jurisdiction-override (modules/tax/server/settings-routes.ts).
            // Never granted to Manager when the routes shipped, so every item-form
            // Tax picker 403'd on load for Manager (#10054) — same posture as the
            // tenants/kds trio above: read + write + write.location, since tax
            // settings ARE per-location overridable (unlike financials sub-accounts).
            'settings.modules.tax.read',
            'settings.modules.tax.write',
            'settings.modules.tax.write.location',
            // Settings UX shell — 9 per-area read permissions (S-Boot-26 PR-8e).
            // Each area requires explicit grant; Owner has '*' wildcard covering all.
            'settings.pages.merchant-services.terminals.read',
            // Merchant Services 7-tab settings surface (merchant-services-v2-parity
            // PR-B, plan §5 — 14 new keys). Granted to Manager only, same posture
            // as the pre-existing `.terminals.read` key above (Owner covers all via
            // '*'; Supervisor/Staff are not granted page-nav access to this area).
            // Backend routes (routes-merchant-services.ts) are gated by the
            // separate settings.modules.payments.{read,write,write.location} perms
            // (Convention 7) — these are UI page-nav (Layer-2) keys only.
            'settings.pages.merchant-services.providers.read',
            'settings.pages.merchant-services.providers.write',
            'settings.pages.merchant-services.merchant-accounts.read',
            'settings.pages.merchant-services.merchant-accounts.write',
            'settings.pages.merchant-services.terminal-assignments.read',
            'settings.pages.merchant-services.terminal-assignments.write',
            'settings.pages.merchant-services.devices.read',
            'settings.pages.merchant-services.devices.write',
            'settings.pages.merchant-services.wallets.read',
            'settings.pages.merchant-services.wallets.write',
            'settings.pages.merchant-services.surcharging.read',
            'settings.pages.merchant-services.surcharging.write',
            'settings.pages.merchant-services.ach.read',
            'settings.pages.merchant-services.ach.write',
            // Dual Pricing tab (#7657, docs/plans/dual-pricing-settings-write-path-
            // 7657a.md Task 7) -- same posture as the 7-tab surface above (Manager
            // only; Owner covers all via '*'; Supervisor/Staff not granted).
            'settings.pages.merchant-services.dual-pricing.read',
            'settings.pages.merchant-services.dual-pricing.write',
            'settings.pages.printers.read',
            // Settings Audit Priority Ledger, Tier 2: Dining settings page's own
            // page-nav permission, replacing its prior borrow of
            // room-layouts.rooms.read. Same posture as the printers/cash-handling
            // page reads above (Manager only; Owner covers all via '*').
            'settings.pages.dining.read',
            // Settings ▸ Modules self-service page (Entitlements Mega PR G1). Owner
            // holds it via the '*' wildcard; Manager is the tenant-admin surface.
            'settings.pages.modules.read',
            'settings.pages.taxes.read',
            'settings.pages.receipts.read',
            // Comms operational perms (MEGA-3). Manager can read send-log +
            // suppression list and add manual suppressions; the send + un-suppress
            // are Owner-only (covered by the Owner '*' wildcard).
            'comms.send-log.read',
            'comms.suppression-list.read',
            // Marketing campaigns (Campaigns Phase 2). Manager can author campaigns
            // and view deliverability status; campaign SEND approval and sending-
            // domain registration stay Owner-only (covered by the '*' wildcard) —
            // authoring never implies authority to dispatch.
            'comms.campaigns.read',
            'comms.campaigns.write',
            'comms.sending-domains.read',
            // Inbound inbox permissions (WS-5 S21/S26). Owner-only via OWNER_ONLY_ALLOWLIST
            // (scripts/check-route-grant-literal-coverage.cjs) — frontend-only synthetic gate;
            // no server route decorated with these permissions. Manager may be granted these
            // in a follow-up once the UX ships to GA.
            // 'comms.inbound.unmatched.read' and 'comms.inbound.unmatched.claim' are
            // intentionally absent from this list; see OWNER_ONLY_ALLOWLIST.
            'settings.pages.cash-handling.read',
            'settings.pages.kds.routing.read',
            'settings.pages.kds.setup.read',
            // #9120: the remaining five KDS settings PAGE reads. Manager already
            // holds settings.modules.kds.read AND .write (below) plus the routing +
            // setup page reads -- a role that can WRITE KDS module settings but
            // cannot READ the stations / prep-times / bump-bar pages is a hole, not
            // a policy. Read-only, same module, sibling precedent in this very
            // block; kitchen-facing config is squarely a manager task.
            'settings.pages.kds.stations.read',
            'settings.pages.kds.alert-profiles.read',
            'settings.pages.kds.bump-bar.read',
            'settings.pages.kds.prep-times.read',
            'settings.pages.kds.general.read',
            'settings.pages.permissions.read',
            'settings.pages.permissions.users.read',
            'settings.pages.reservations.read',
            'reservations.bookable-services.read',
            'reservations.bookable-services.write',
            // Reservation booking CRUD (host-stand / floor timeline, S3-PR-d).
            'reservations.bookings.read',
            'reservations.bookings.write',
            'settings.pages.loyalty.read',
            'settings.pages.printers.routing.read',
            // Profit Centers settings surface (2026-05-24 plan).
            // Manager reads all + writes profit-centers + terminals + payment-device
            // pairing + POS-device claim/archive. Locations.delete stays Owner-only.
            'settings.pages.locations.read',
            'settings.pages.locations.write',
            'settings.locations.write',
            'settings.locations.delete',
            'settings.pages.profit-centers.read',
            'settings.pages.profit-centers.write',
            'settings.profit-centers.write',
            'settings.profit-centers.delete',
            'settings.pages.terminals.read',
            'settings.pages.terminals.write',
            'settings.terminals.write',
            'settings.terminals.delete',
            'settings.pages.payment-devices.read',
            'settings.payment-devices.pair',
            'settings.payment-devices.unpair',
            'settings.payment-devices.test-transaction',
            'settings.pages.pos-devices.read',
            'settings.pos-devices.claim',
            'settings.pos-devices.archive',
            'tenants.locations.read',
            'tenants.locations.update',
            'catalog.movements.read',
            'catalog.transfer.create',
            'catalog.on-hand.read',
            // On-hand inventory valuation export — exposes COST + extended VALUE data.
            // Must NOT reuse catalog.on-hand.read (held by Cashier/Server/Supervisor).
            // Mirrors the financials read/export split (financials.statements.read vs
            // financials.statements.export). Owner inherits via the '*' wildcard.
            'catalog.inventory-valuation.export',
            // Item catalog CSV export — exposes COST + price. Must NOT reach
            // cashier/server/supervisor. Owner via '*'.
            'catalog.items.export',
            // PR-4a: vendor + purchase-order authoring permissions.
            'catalog.vendor.read',
            'catalog.vendor.create',
            'catalog.vendor.update',
            'catalog.vendor.archive',
            // Stream 1 (catalog-pricing-authority): price-level admin CRUD.
            // Manager gets both read + write (create/update/archive) — same
            // admin-tier surface as vendor authoring above, not the broader
            // cashier/server/supervisor on-hand-read scope. Mirrors
            // catalog.transfers.commit's manager-only write scoping.
            'catalog.pricing.read',
            'catalog.pricing.write',
            'catalog.purchase-order.read',
            'catalog.purchase-order.create',
            'catalog.purchase-order.update',
            'catalog.purchase-order.approve',
            'catalog.purchase-order.cancel',
            // Low-stock report + suggested-PO generation (item 2, POS-vs-Lightspeed/
            // Clover gap closure) — granted alongside purchase-order authoring since
            // the report's "Generate suggested PO" action feeds directly into it.
            'catalog.low-stock.read',
            // #5934: setting the reorder point / par level / reorder qty the report
            // above reads. MANAGER-ONLY, deliberately narrower than the report's
            // read — supervisor sees what is below par, but reorder policy is a
            // purchasing decision. Mirrors catalog.pricing.write's scoping, the
            // sibling per-item-location config surface, rather than the broader
            // on-hand-read scope.
            'catalog.reorder-settings.write',
            // Recorded expenses: submit and approve are financial-critical. The activating
            // producer must enforce assigned approval and refuse creator self-approval.
            'ap.expense.read',
            'ap.expense.write',
            'ap.expense.submit',
            'ap.expense.approve',
            // AP Phase 2: bill entry workflow (list / create / submit-for-approval).
            // Manager owns vendor + PO authoring (above); bill entry continues the
            // same operations surface into payables. Phase 2.5 adds the
            // payment-terms library write (POST /payment-terms) so Managers can
            // create custom tenant-scoped terms alongside the built-in seeds; the
            // read surface goes through ap.bill.read.
            'ap.bill.read',
            'ap.bill.write',
            'ap.bill.submit',
            // AP Phase 3: approval decision surface — Manager can approve and void bills.
            'ap.bill.approve',
            'ap.bill.void',
            // AP delegations: Manager (approver) can manage their own delegations.
            'ap.approval.delegate.self',
            // AP Phase A: bill hold/release + hold-code library management.
            'ap.bill.hold',
            'ap.hold-code.read',
            'ap.hold-code.write',
            'ap.terms.write',
            // AP Phase 5: payment batch workflow.
            // Manager (AP admin/manager equivalent) gets all four payment permissions.
            'ap.payment.read',
            'ap.payment.batch',
            'ap.payment.execute',
            'ap.payment.void',
            'ap.credit.read',
            'ap.credit.write',
            'ap.credit.void',
            // AP Write Checks: manager gets full check surface.
            'ap.check.read',
            'ap.check.write',
            'ap.check.print',
            'ap.check.reprint',
            'ap.check.void',
            'ap.check.override-number',
            // AP ACH: manager gets full vendor ACH surface + NACHA export.
            'ap.vendor.read',
            'ap.vendor.ach.manage',
            'ap.vendor.ach.verify',
            'ap.vendor.ach.replace',
            'ap.payment.export-ach',
            // AR Center V1: manager gets full AR operational surface.
            'ar.invoice.list',
            'ar.invoice.create',
            // Session 4 (D11): granted alongside ar.invoice.create -- every role
            // that can create an AR invoice can select which document template it
            // uses. Do not add this to a role that lacks ar.invoice.create without
            // re-checking D11's rationale (packages/contracts/src/document-templates/permissions.ts).
            'document-templates.template.use',
            'ar.invoice.read',
            'ar.invoice.post',
            'ar.invoice.void',
            'ar.invoice.refund',
            'ar.invoice.update',
            'ar.invoice.delete',
            // Billing Quotes Phase 2: managers own the full quote lifecycle.
            'quotes.quote.read',
            'quotes.quote.write',
            'quotes.quote.accept-decline',
            'quotes.quote.convert',
            // Write-Ups: same roles that manage the full quote lifecycle also
            // manage the PO-handoff document it produces.
            'write-ups.write-up.read',
            'write-ups.write-up.write',
            // Write-Ups settings (nav flag + Settings page) are gated separately from
            // the CRUD permission above (modules/write-ups/server/settings-routes.ts) --
            // grant read+write here so a role with full write-up access can also see
            // the nav tab and use the settings page, same posture as the tax settings
            // pairing above.
            'settings.modules.write-ups.read',
            'settings.modules.write-ups.write',
            'ar.receipt.list',
            'ar.receipt.create',
            'ar.receipt.read',
            'ar.receipt.receive-payment',
            'ar.receipt.void',
            'ar.receipt.refund',
            'ar.application.apply',
            'ar.application.reverse',
            'ar.adjustment.credit-memo',
            'ar.credit.read',
            'ar.credit.issue',
            'ar.credit.void',
            'ar.adjustment.write-off',
            'ar.external.adjustment',
            'ar.aging.read',
            'ar.statement.list',
            'ar.statement.read',
            'ar.dunning.read',
            'ar.dunning.manage',
            'ar.portal.token.create',
            'ar.settings.manage',
            'ap.settings.manage',
            // PR-4a: stock-ledger admin reads + allocation lifecycle write surface.
            'catalog.allocations.read',
            'catalog.allocations.create',
            'catalog.allocations.fulfill',
            'catalog.allocations.release',
            'catalog.lots.read',
            'catalog.cost-layers.read',
            'catalog.recipes.read',
            'catalog.recipes.create',
            'catalog.recipes.update',
            // PR-4b: admin composite write surface (HTTP routes for the
            // postReceiving / commitTransfer / commitCycleCount composites
            // from PR-3d). Manager-only — these mutate the cost-layer
            // ledger + on-hand projection across multiple atoms in one tx,
            // which is operations territory above supervisor's typical
            // shift-level scope.
            'catalog.receivings.post',
            'catalog.transfers.commit',
            'catalog.cycle-counts.commit',
            'gift-cards.balance.read',
            'gift-cards.personal-data.read',
            'gift-cards.code.reprint',
            'gift-cards.funding-review.resolve',
            'gift-cards.validate',
            'gift-cards.issue',
            'gift-cards.reload',
            'gift-cards.redeem',
            'memberships.plan.read',
            'memberships.member.read',
            'memberships.plan.sell',
            'memberships.plan.create',
            'memberships.loyalty.redeem',
            // Stream 1 (catalog-pricing-authority): assign/clear a tier's catalog
            // price level. Manager-only write — mirrors catalog.pricing.write's
            // manager-tier-only scoping above (not extended to supervisor/cashier/
            // server), since this is the same admin-pricing-authority surface from
            // the memberships side.
            'memberships.tiers.write',
            'gift-cards.void',
            'gift-cards.adjust',
            'gift-cards.freeze',
            'gift-cards.refund',
            'gift-cards.holds.manage',
            // 'gift-cards.erasure' is owner-only by design (impl-plan §1: tightening
            // erasure to owner-only is a 26b-3 follow-up). Owner has `*` wildcard,
            // so owner retains it; manager does not.
            'gift-cards.manage-designs',
            'gift-cards.manage-promotions',
            // Templates (card_types) — manager-only; mirrors manage-designs scope
            'gift-cards.manage-card-types',
            // PR9 W2: gift-card policy settings (General/Security/Accounting tabs).
            // Manager-level — tenant policy + accounting-affecting kill switches.
            'gift-cards.manage-settings',
            'gift-cards.view-balance-audit',
            'gift-cards.import',
            'gift-cards.view-dlq',
            // PR8 §A: Tier-3 accounting confirmation to approve/decline a pending
            // breakage-recognition run (recognizes income + posts Dr 2200 / Cr 700000).
            // Manager-level — accounting territory above supervisor's shift scope.
            'gift-cards.breakage.approve',
            // Task 3 of #6771: resolves a parked storefront.payment.captured_unissued
            // DLQ row — refunds the unspent captured payment or attests an
            // out-of-band manual issuance. A distinct, narrower permission than the
            // read-only gift-cards.view-dlq (supervisor-visible): this is a
            // write/financial action, so manager-only, not supervisor.
            'gift-cards.dlq.resolve',
            // Gift-card sales + breakage reports (PR10 — replaces stubs).
            // Read-only aggregation of rm_gift_card_lifecycle. Manager-level.
            'reports.gift-cards.read',
            // KDS ticket permissions per modules/kds/server/routes.ts (PR-12 Slice 5b).
            // Task 11 of the KDS app plan grants all 5 to manager — recall + void
            // stay manager-only (recall has PIN-window enforcement deferred to PR-15).
            'kds.ticket.read',
            'kds.ticket.bump',
            'kds.ticket.serve',
            'kds.ticket.recall',
            'kds.ticket.void',
            // KDS extended ops (PR-12 Slice 12b.1 — hold/refire/acknowledge-delta).
            'kds.ticket.hold',
            'kds.ticket.refire',
            'kds.ticket.acknowledge-delta',
            // KDS station-message board (S4-PR-c, 04-KDS.md §H) — same operator
            // posture as kds.ticket.hold / acknowledge-delta (no step-up, no extra
            // audit; reversible soft-dismiss). permission-metadata.ts says so.
            'kds.station-message.send',
            // KDS send-reliability ACK ingest (S4b-A2b) — same operator posture; the
            // station client posts delivery/display/heartbeat ACKs.
            'kds.order-status.ack',
            // KDS send-reliability console read (S4b-D) — Active/Needs-Attention/
            // History tabs + station health + diagnostics. Non-destructive; granted to
            // every transaction tier (manager + supervisor + staff), like kds.ticket.read.
            'kds.order-status.read',
            // KDS send-reliability recovery (S4b-C) — retry/reroute/resolve/clear/
            // soft-delete a stuck send. Manager + supervisor only (destructive +
            // audited); staff intentionally excluded.
            'kds.order-status.recover',
            // POS course-fire (manager + supervisor + server) — manual course advance.
            'pos.course.fire-manually',
            // POS cart + catalog reads — every transaction-tier role.
            'pos.cart.read',
            'catalog.pos.read',
            // POS lease write — anyone operating a register binds the device to
            // their session. Mirrors pos.drawer.{open,close} scope.
            'pos.lease.write',
            // Workforce timeclock punch — every employee clocks in/out every shift.
            'workforce.timeclock.punch',
            // House accounts — PR-13.d-1. Manager and above can view AR ledger
            // and perform lifecycle commands (close / reactivate / write-off).
            'house-accounts.read',
            'house-accounts.lifecycle.write',
            'house-accounts.manage',
            'lodging.folios.read',
            'lodging.folios.write',
            'pos.device.read',
            'pos.device.write',
            'pos.check.read',
            // Virtual Terminal (VT PR-2) — card-only Quick Charge / Send-a-link
            // collection surface. Gated on this permission alone, never on
            // pos.check.read (POS isolation rule 9; I-6).
            'pos.virtual-terminal.access',
            // VT charge tabs (#14612): the per-location switch + its settings page.
            'settings.pages.virtual-terminal.read',
            'settings.pages.virtual-terminal.write',
            'customer-billing.vt-settings.read',
            'customer-billing.vt-settings.write',
            // VT PR-4 (#8724) -- narrower-permissioned VT refund/void action,
            // distinct from the broad commerce.order.refund (D4, least-privilege).
            'pos.virtual-terminal.refund',
            // #14421 -- the bulk stop lever (revoke every live VT pay link). Manager and owner only
            // (owner via '*'): cashier, server and supervisor deliberately do not hold it.
            'pos.virtual-terminal.revoke-all-links',
            // #14288 / #14478: may approve a VT card charge at or above $1,000 by PIN (owner ruling
            // 2026-10-07: manager, supervisor and owner by default; cashier and server do not).
            'pos.virtual-terminal.approve-over-limit',
            // J14 (#14478, #15589): may approve, as the second person, a refund that brings a sale to $50
            // or more (owner rulings on #14478: manager, supervisor and owner; cashier and server do not).
            'payments.tender.refund.approve',
            'pos.check.write',
            // Custom/open-price line items get a dedicated permission, distinct
            // from the routine pos.check.write (H5, 2026-07-18 POS
            // systemic-debugging audit) — granted to the same tier as
            // pos.check.write today; a tenant can later narrow it without
            // touching other cart-write actions.
            'pos.check.custom-item',
            'pos.check.split',
            'pos.check.merge',
            'pos.check.unlock',
            'pos.lease.read',
            // Operator-context-bootstrap permissions (S-Boot OCB PR-1).
            // Spec: docs/superpowers/specs/2026-05-20-operator-context-bootstrap-design.md §3
            // ADR: ADR-0060. takeover is manager+ only — the other 7 are all-role.
            'pos.bootstrap.role.read',
            'pos.bootstrap.location.read',
            'pos.bootstrap.profit-center.read',
            'pos.bootstrap.terminal.read',
            'pos.session.start',
            'pos.session.end',
            'pos.session.takeover',
            'pos.terminal.resume',
            // Granular pos.drawer permissions per docs/superpowers/plans/2026-05-12-sboot-26-pr-7-shifts-drawer-eod.md.
            // Manager opens/closes drawer, reviews variance, runs Z-report (eod).
            'pos.drawer.open',
            'pos.drawer.close',
            'pos.drawer.variance-review',
            'pos.drawer.eod',
            // Force-close override (2026-07-18 POS systemic-debugging audit C2) —
            // Manager+/Supervisor+ only, distinct from the routine self-close
            // pos.drawer.close (granted to every operational role).
            'pos.drawer.force-close',
            // Cash-removal gate (2026-08 drawer cash-control-gates fix) — paid_out/
            // cash_drop/cash_pickup move cash out of the drawer with an unbounded
            // amount; previously shared pos.drawer.open (same tier as opening a
            // session). Manager+/Supervisor+ only, mirrors pos.drawer.force-close.
            'pos.drawer.cash-remove',
            // Modifier-level 86 (mark out-of-stock / lift) — mounted route for the
            // previously-orphaned domain module (C8 half A, 2026-07-18 POS
            // systemic-debugging audit). Manager+/Supervisor+ only.
            'pos.modifier-86.write',
            'pos.modifier-86.read',
            // PR-16: Manager Recovery Console permissions (§44.10)
            'pos.recovery.search',
            'pos.recovery.stuck-auth-capture',
            'pos.recovery.stuck-auth-void',
            'pos.recovery.orphan-payment-match',
            'pos.recovery.lease-force-release',
            'pos.recovery.device-retire',
            'pos.recovery.device-mark-lost',
            'pos.recovery.kds-refire',
            'pos.recovery.printer-retry',
            'pos.recovery.check-reversal-manage',
            'pos.recovery.duplicate-payment-void',
            'pos.recovery.lock.escalate',
            // Agent-pairing: manager pairs/re-pairs the native print agent during setup.
            'pos.agent.pair',
            'payments.terminal.connect',
            'payments.terminal.release',
            'payments.terminal.status',
            'payments.terminal.auth-card',
            'payments.terminal.prompt-tip',
            'payments.terminal.display-message',
            'payments.terminal.cancel',
            // Workforce admin — employees + compensation + identity-merge ops.
            'workforce.employees.manage',
            'workforce.employees.write',
            'workforce.employees.number.correct',
            'workforce.compensation.manage',
            'workforce.compensation.approve',
            // SOX 4-eyes approval of a pending W-2/1099 classification-change
            // request (ticket #8474) — same tier as the compensation-approve
            // precedent above. Owner picks up via '*'.
            'workforce.classification.approve',
            'workforce.identity.merge.candidate.read',
            'workforce.identity.merge.candidate.reject',
            'workforce.identity.merge',
            'workforce.identity.unmerge',
            'workforce.timeclock.edit',
            // Manager-initiated live clock-in/out on an employee's behalf
            // (managerPunchIn/managerPunchOut) — same Manager+Supervisor scoping as
            // workforce.timeclock.edit, per
            // docs/superpowers/specs/2026-08-02-manager-clockin-design.md.
            'workforce.timeclock.punch.others',
            'workforce.timeclock.approve',
            'workforce.timeclock.dispute',
            'workforce.eeo.aggregate.read',
            // Geofencing (PR C: API surface). devices.approve is Manager-only —
            // mirrors the pos.drawer.eod precedent (Manager+ only, withheld from
            // Supervisor) below in the Supervisor block's comment.
            'workforce.geofencing.read',
            'workforce.geofencing.write',
            'workforce.geofencing.review',
            'workforce.geofencing.devices.approve',
            // Workforce V2 PR1 read surface
            'workforce.employees.read',
            'workforce.compensation.read',
            'workforce.timeclock.read',
            // Payroll settings (schedule, overtime, breaks, etc.) — read/write split,
            // following the workforce.compensation.read/write convention. Manager-tier
            // admin config; Owner covers it via the '*' wildcard.
            'workforce.payroll-settings.read',
            'workforce.payroll-settings.write',
            // Workforce V2 PR1.5a — org-structure CRUD
            'workforce.org.read',
            'workforce.org.manage',
            // PTO lifecycle (S-Boot-21b-5 PR-3). Manager-tier grants only here;
            // the full role wiring (employee + supervisor surfaces) lands in
            // S-Boot-21b-5 PR-5 (RBAC + permission groups) per the build plan
            // (docs/superpowers/plans/2026-06-10-workforce-pto.md Task 13).
            // `workforce.pto.review` gates approve/deny/cancel (Option A — pto.cancel retired);
            // `workforce.pto.read` gates the review queue listing; `workforce.pto.request`
            // is included so managers can submit PTO on an employee's behalf.
            // `workforce.pto.adjust` gates manual adjustments, blackout-date CRUD,
            // and year-end reset (S-Boot-21b-5 PR-4).
            'workforce.pto.request',
            'workforce.pto.review',
            'workforce.pto.read',
            'workforce.pto.adjust',
            'workforce.pto.policy.manage',
            // Manager messages panel (Task 18) — read the thread list + messages at
            // the manager's accessible locations, and reply as a manager.
            'workforce.messages.read',
            'workforce.messages.write',
            // Manager announcements (manager-messaging-and-announcements plan) —
            // view announcement history within scope, and create a location- or
            // department-wide announcement within the manager's accessible scope.
            // Dedicated permissions per spec — NOT reusing workforce.messages.*.
            'workforce.announcements.read',
            'workforce.announcements.write',
            // Manager Dashboard (2026-07-26-manager-dashboard plan, Task 3) — gates
            // the dashboard route (beforeLoad + nav item, Task 1) and the backend
            // GET /workforce/dashboard/priorities route (Task 3).
            'workforce.dashboard.view',
            // Tip reports — read-only access to tip summary, shift detail, distribution ledger,
            // FLSA compliance, and cash/card reconciliation. Manager/Location Admin tier.
            'workforce.tips.reports.read',
            // Scheduling surface (PR6a + PR6b, ticket #4758/#4773). View is read-only;
            // manage covers the full command surface (create/edit shifts, templates, availability);
            // publish is a controls boundary for releasing a schedule to employees.
            'workforce.scheduling.view',
            'workforce.scheduling.manage',
            'workforce.scheduling.publish',
            // Self-service settings (which employee-portal modules are visible to
            // employees) — same role tier as scheduling.
            'workforce.self-service-settings.view',
            'workforce.self-service-settings.manage',
            // Tax groups — settings.modules.tenants.read parity. /settings/tax is already
            // page-gated by settings.modules.tenants.read; without this the API fetch 403s.
            'tax.groups.read',
            'tax.groups.write',
            'tax.rates.read',
            'tax.rates.write',
            // Commerce write surface — base grants. Above-threshold operations are
            // gated by the manager-PIN elevation substrate (PR #875).
            'commerce.adjustment.apply',
            'commerce.service-charge.apply',
            'commerce.service-charge.waive',
            'commerce.order.add-line',
            // Catalog hierarchy management (PR-2 Task 6).
            // Manager can view + author + update + archive the Department › Sub-Department ›
            // Category taxonomy and access the merchandising settings page.
            'catalog.hierarchy.read',
            'catalog.hierarchy.create',
            'catalog.hierarchy.update',
            'catalog.hierarchy.archive',
            'settings.pages.merchandising.read',
            // Catalog Settings page (#12601): tenant-wide margin-floor / low-stock-
            // threshold settings. Manager gets read + write, same tier as the
            // merchandising settings page immediately above.
            'settings.modules.catalog.read',
            'settings.modules.catalog.write',
            // Tab Board (Tab Board PR-T1). Manager gets view + pull + grab-group config.
            // view + pull are also granted to supervisor/cashier/server (all roles with
            // pos.lease.write — operators who work tabs should be able to view and pull).
            // pos.tab-grab.manage (facility grab-group authoring) stays manager-only.
            'pos.tab-board.view',
            'pos.pickup-board.view',
            'pos.pickup-board.mark-ready',
            'pos.check.pull',
            'pos.check.view-all', // floor-oversight: see all tabs at the location
            'pos.check.clone',
            'pos.tab-grab.manage',
            // Catalog Rule Inheritance PR-2. Coursing-rule authoring is a menu/ops-admin
            // action — manager-only, same precedent as pos.tab-grab.manage. Owner holds
            // it via the '*' wildcard.
            'pos.coursing.manage',
            // Receipt-printing Phase 0.
            'pos.receipts.build',
            'pos.receipts.email',
            // VT pay-link delivery PR-2 — granted to exactly the roles that already
            // hold pos.receipts.email. Owner holds it via the '*' wildcard; custom
            // roles get no automatic grant.
            'pos.receipts.sms',
            // Printer-dispatch substrate capstone — physical print dispatch.
            'pos.print.dispatch',
            // Printer + routing-rule CRUD — printer-dispatch substrate Unit F1.
            // Manager gets both read and write. Owner holds via '*' wildcard.
            'pos.printers.read',
            'pos.printers.write',
            'pos.printer-routing.read',
            'pos.printer-routing.write',
            // Print-job DLQ admin — read failed-permanent print jobs and recover
            // (re-queue) them. Ops triage capability for failed-print recovery.
            // Owner holds via '*' wildcard; Manager is the minimum role that
            // should be able to inspect and recover DLQ entries.
            'pos.print.dlq-read',
            'pos.print.dlq-recover',
            // Assistant — manager gets use + code.ground + read + feedback + admin.manage + read-any.
            // Owner inherits all via '*'. read-any is audited (support review).
            'assistant.conversation.use',
            'assistant.code.ground',
            'assistant.conversation.read',
            'assistant.feedback.write',
            'assistant.admin.manage',
            'assistant.conversation.read-any',
            // Support/feedback — same 8 roles as assistant.feedback.write (D20).
            'support.feedback.submit',
            'support.feedback.read',
            'support.roadmap.view',
            'support.roadmap.vote',
            // Insights - manager gets all 6 non-admin permissions + P7 goals vertical.
            'insights.query.create',
            'insights.query.execute',
            // D1: manager is a power role — may inspect the raw compiled SQL behind an
            // answer. Lower roles (supervisor / accountant-*) get query.execute but not
            // raw-SQL inspection. Owner gets it via '*'.
            'insights.sql.view',
            'insights.registry.read',
            'insights.registry.author',
            'insights.review.manage',
            'insights.settings.manage',
            'insights.settings.narration-profile.manage',
            'insights.goals.read',
            'insights.goals.write',
            'insights.simulations.read',
            'insights.simulations.write',
            'insights.annotations.read',
            'insights.annotations.write',
            // commerce.orders.write — ADR-0101 assistant write-action confirm
            // surface (apply order discount, add order note). Each mutation runs
            // through the operator confirmation envelope and an audited
            // assistant.write_action_log INSERT (see audit-manifest entry), and the
            // permission is held alongside the existing pos.* / payments.tender.*
            // override-style permissions Manager already carries.
            'commerce.orders.write',
            // Sales history lifecycle commands (Task 4 / PR1f-lifecycle).
            // Allows reopening a closed order for additional items. Granted to
            // Cashier, Supervisor, Manager.
            'commerce.order.reopen',
            // Allows marking items as exchanged on an order. Granted to Supervisor
            // and Manager only.
            'commerce.order.exchange',
            // Allows initiating a return (reversal order with inventory-restore posture).
            // Granted to Supervisor and Manager only.
            'commerce.order.return',
            // Allows posting compensation after order close. Granted to Manager only.
            'commerce.order.post-close-comp',
            // S-Boot-26 PR-6 customer-attach routes. Manager gets standard attach
            // (commerce.customer.attach) and the retroactive variant that backfills
            // a customer onto a closed/completed order.
            'commerce.customer.attach',
            'commerce.customer.retroactive-attach',
            // S-Boot-26 PR-6 Task 7: gift-card sale at POS. Granted to cashier,
            // supervisor, and manager (everyone who operates a register).
            'commerce.gift-card-sale.create',
            // Room layouts — manager can read and write rooms (full CRUD).
            'room-layouts.rooms.read',
            'room-layouts.rooms.write',
            // Appointments (MEGA-1 S2) — manager gets full config + booking + settings authority.
            'appointments.services.read',
            'appointments.services.write',
            'appointments.providers.read',
            'appointments.providers.write',
            'appointments.resources.read',
            'appointments.resources.write',
            'appointments.bookings.read',
            'appointments.bookings.write',
            'appointments.calendar.read',
            'settings.modules.appointments.read',
            'settings.modules.appointments.write',
            'settings.modules.appointments.write.location',
            'settings.pages.appointments.read',
            'settings.pages.appointments.write',
        ],
        isOwner: false,
        isAssignable: true,
    },
    supervisor: {
        id: 'supervisor',
        displayName: 'Supervisor',
        description: 'Shift supervisor or lead server. Front-of-house operations; no financials.* visibility.',
        permissions: [
            'payment-hub.settings.read',
            'payment-hub.requests.read',
            'commerce.order.create',
            'commerce.order.read',
            'commerce.order.update',
            'commerce.order.cancel',
            'commerce.order.void',
            'payments.capture.read',
            'payments.tender.read',
            'payments.tender.void',
            'payments.vault.store',
            'payments.vault.read',
            'workforce.shift.read',
            'workforce.shift.close',
            'workforce.shift.transfer',
            // Workforce V2 PR1 — supervisors can view roster + timeclock entries for review
            'workforce.employees.read',
            'workforce.timeclock.read',
            // Workforce V2 PR1.5a — org structure reads (hire wizard pickers)
            'workforce.org.read',
            // PTO self-service (S-Boot-21b-5 PR-5): supervisors can submit requests.
            'workforce.pto.request',
            'catalog.adjustment.create',
            'catalog.items.read',
            'catalog.movements.read',
            'catalog.receiving.create',
            'catalog.receiving.post',
            'catalog.on-hand.read',
            // PR-4a: vendor + PO authoring — supervisor scope (no archive/approve/cancel).
            'catalog.vendor.read',
            'catalog.vendor.create',
            'catalog.vendor.update',
            // Stream 1 (catalog-pricing-authority): price-level admin read only —
            // supervisor can see price levels for context, no create/update/archive
            // (write stays Manager+, mirrors the vendor split above).
            'catalog.pricing.read',
            'catalog.purchase-order.read',
            'catalog.purchase-order.create',
            'catalog.purchase-order.update',
            // Low-stock report + suggested-PO generation (item 2, POS-vs-Lightspeed/
            // Clover gap closure) — granted alongside purchase-order authoring since
            // the report's "Generate suggested PO" action feeds directly into it.
            'catalog.low-stock.read',
            // PR-4a: supervisor needs allocation read + create/release for
            // online-order pre-payment holds. Fulfill stays manager+ (matches
            // void/refund pattern: lifecycle terminal-flip is a manager call).
            'catalog.allocations.read',
            'catalog.allocations.create',
            'catalog.allocations.release',
            'catalog.lots.read',
            'catalog.recipes.read',
            // Customers — granular vocabulary per docs/business-rules/customers-module.md.
            // No merge/verify-ach/archive — those stay Manager+ per the void/refund
            // pattern.
            'customers.view',
            'customers.manage',
            'customers.tags.manage',
            'customers.payment-methods.view',
            'customers.payment-methods.add',
            'customers.payment-methods.set-default',
            // Customer profiles V2 first-slice (sub-PR 8f); spec §4.6.
            'customers.contact.read',
            'customers.contact.write',
            'customers.financial.read',
            'customers.stored-value.read',
            'customers.membership.read',
            'customers.activity.read',
            'customers.tags.read',
            'customers.tags.write',
            'customers.relationships.read',
            'customers.documents.read',
            'customers.notes.read',
            'customers.notes.write',
            'customers.smart-tags.read',
            'customers.ar-aging.read',
            'customers.manage-contacts',
            'catalog.modifiers.read',
            'catalog.tax.read',
            'tabs.transfer',
            'tabs.close',
            'gratuity.modify',
            'tenants.settings.read',
            // Settings spine (S-Boot-26 PR-8b cluster) — per conv-settings-spine.md §1 Convention 7.
            // Supervisor gets read + write.location (per-location override scope).
            'settings.modules.tenants.read',
            'settings.modules.tenants.write.location',
            // Document Templates settings (ADR-0174) -- read only, mirroring the
            // settings.modules.tenants.read grant immediately above (Supervisor
            // holds no plain .write for tenants either).
            'settings.modules.document-templates.read',
            'settings.modules.kds.read',
            'settings.modules.kds.write.location',
            // Match the six page gates to Supervisor's existing KDS API access (#10089).
            'settings.pages.kds.routing.read',
            'settings.pages.kds.stations.read',
            'settings.pages.kds.alert-profiles.read',
            'settings.pages.kds.bump-bar.read',
            'settings.pages.kds.prep-times.read',
            'settings.pages.kds.general.read',
            // Financials settings — read-only visibility into GL sub-account configuration
            // (deferred-revenue-s4). Supervisor can see the settings page; write stays with manager/owner.
            'settings.modules.financials.read',
            // Profit Centers settings surface (2026-05-24 plan).
            // Supervisor: reads + payment-device pair/unpair/test only.
            // No writes to locations/profit-centers/terminals.
            'settings.pages.locations.read',
            'settings.pages.profit-centers.read',
            'settings.pages.terminals.read',
            'settings.pages.payment-devices.read',
            'settings.payment-devices.pair',
            'settings.payment-devices.unpair',
            'settings.payment-devices.test-transaction',
            'settings.pages.pos-devices.read',
            'tenants.locations.read',
            // Role catalog visibility (role-manager rework PR-3 per ADR-0061).
            // Supervisor can view roles for ops awareness; no mutation rights
            // (Owner-only via the '*' wildcard). Matches the read pattern of
            // tenants.role-assignments.read which manager carries.
            'tenants.roles.read',
            'reports.read',
            'reports.commerce.read',
            // Billing Reports tab (Reports foundation task 1) — same tier as
            // reports.commerce.read above.
            'reports.billing.read',
            // F&B reporting surface (S12 Phase-1, ADR-0139) — read-only, same
            // tier as reports.commerce.read above.
            'reports.fnb-reporting.read',
            // Dashboard landing surface (dashboard-v2 PR1) — operator landing page;
            // revenue tile is independently gated by 'commerce.revenue.view' on the client.
            'commerce.dashboard.view',
            'gift-cards.balance.read',
            'gift-cards.validate',
            'gift-cards.issue',
            'gift-cards.redeem',
            'memberships.plan.read',
            'memberships.member.read',
            'memberships.plan.sell',
            'memberships.plan.create',
            'memberships.loyalty.redeem',
            'gift-cards.holds.manage',
            'gift-cards.view-balance-audit',
            'gift-cards.view-dlq',
            // MP-1b PR #969/#970 detector queue surfaces; resolve action gated to manager per audit-log weight (PR #971/#972).
            'financials.findings.read',
            'financials.deposit.read',
            // #8617: narrow customer-deposit lookup — the POS customer-attach
            // "open deposits" nudge is reachable by Supervisor; the broader
            // financials.statements.read is not (and shouldn't be, for this tier).
            'financials.customer-deposits.lookup',
            // Reconciliation workflow (Phase 1 plan 2026-06-04). Supervisor gets read only.
            'financials.reconciliation.read',
            // Billing Deposits & Reconciliation surface (Plan D) -- Supervisor gets
            // read only, same tier posture as financials.reconciliation.read above.
            'customer-billing.reconciliation.read',
            // Billing workspace read-only tier (PR-2A) -- same posture as
            // customer-billing.reconciliation.read directly above.
            'customer-billing.workspace.read',
            'customer-billing.subscription.read',
            'customer-billing.plan.read',
            'customer-billing.pay-link.read',
            // KDS ticket permissions per modules/kds/server/routes.ts (PR-12 Slice 5b).
            // Supervisor is the expo tier — read/bump/serve. Recall + void stay
            // Manager+ per Task 11 of the KDS app plan.
            'kds.ticket.read',
            'kds.ticket.bump',
            'kds.ticket.serve',
            // KDS extended ops (PR-12 Slice 12b.1) — Supervisor handles hold/refire
            // at the expo station + clears modifier-change indicators.
            'kds.ticket.hold',
            'kds.ticket.refire',
            'kds.ticket.acknowledge-delta',
            // KDS station-message board (S4-PR-c, 04-KDS.md §H) — same operator
            // posture as kds.ticket.hold / acknowledge-delta (no step-up, no extra
            // audit; reversible soft-dismiss). permission-metadata.ts says so.
            'kds.station-message.send',
            // KDS send-reliability ACK ingest (S4b-A2b) — same operator posture; the
            // station client posts delivery/display/heartbeat ACKs.
            'kds.order-status.ack',
            // KDS send-reliability console read (S4b-D) — Active/Needs-Attention/
            // History tabs + station health + diagnostics. Non-destructive; granted to
            // every transaction tier (manager + supervisor + staff), like kds.ticket.read.
            'kds.order-status.read',
            // KDS send-reliability recovery (S4b-C) — retry/reroute/resolve/clear/
            // soft-delete a stuck send. Manager + supervisor only (destructive +
            // audited); staff intentionally excluded.
            'kds.order-status.recover',
            // POS course-fire (manager + supervisor + server) — manual course advance.
            'pos.course.fire-manually',
            // POS cart + catalog reads.
            'pos.cart.read',
            'catalog.pos.read',
            // POS lease write — mirrors pos.drawer.{open,close} scope.
            'pos.lease.write',
            // Workforce timeclock punch — every employee clocks in/out every shift.
            'workforce.timeclock.punch',
            // House accounts — PR-13.d-1. Supervisor has read access only.
            // Lifecycle writes (close/reactivate/write-off) require manager+.
            'house-accounts.read',
            'lodging.folios.read',
            'pos.device.read',
            'pos.check.read',
            // pos.check.read (POS isolation rule 9; I-6). Supervisor already
            // carries full pos.check.*/pos.drawer.*/payments.tender.void access --
            // a strictly broader POS operator surface than cashier/server, both of
            // which already carry this. Withholding VT specifically would be an
            // inconsistent gap, not a deliberate restriction.
            'pos.virtual-terminal.access',
            // VT charge tabs (#14612): see the Virtual Terminal settings page (read-only).
            'settings.pages.virtual-terminal.read',
            // VT PR-4 (#8724) -- narrower-permissioned VT refund/void action,
            // distinct from the broad commerce.order.refund (D4, least-privilege).
            'pos.virtual-terminal.refund',
            // #14288 / #14478: may approve a VT card charge at or above $1,000 by PIN (owner ruling
            // 2026-10-07: manager, supervisor and owner by default; cashier and server do not).
            'pos.virtual-terminal.approve-over-limit',
            // J14 (#14478, #15589): may approve, as the second person, a refund that brings a sale to $50
            // or more (owner rulings on #14478: supervisors may approve, with their own clock-in PIN).
            'payments.tender.refund.approve',
            'pos.check.write',
            // Custom/open-price line items get a dedicated permission, distinct
            // from the routine pos.check.write (H5, 2026-07-18 POS
            // systemic-debugging audit) — granted to the same tier as
            // pos.check.write today; a tenant can later narrow it without
            // touching other cart-write actions.
            'pos.check.custom-item',
            'pos.check.split',
            'pos.check.merge',
            'pos.lease.read',
            // Operator-context-bootstrap permissions (S-Boot OCB PR-1).
            // takeover stays manager+ only; supervisor gets the 7 all-role perms.
            'pos.bootstrap.role.read',
            'pos.bootstrap.location.read',
            'pos.bootstrap.profit-center.read',
            'pos.bootstrap.terminal.read',
            'pos.session.start',
            'pos.session.end',
            'pos.terminal.resume',
            // Granular pos.drawer permissions per docs/superpowers/plans/2026-05-12-sboot-26-pr-7-shifts-drawer-eod.md.
            // Supervisor opens/closes drawer + reviews variance; eod (Z-report) stays Manager+.
            'pos.drawer.open',
            'pos.drawer.close',
            'pos.drawer.variance-review',
            // Force-close override (2026-07-18 POS systemic-debugging audit C2) —
            // Manager+/Supervisor+ only.
            'pos.drawer.force-close',
            // Cash-removal gate (2026-08 drawer cash-control-gates fix) — see
            // manager block above for rationale.
            'pos.drawer.cash-remove',
            // Modifier-level 86 (mark out-of-stock / lift) — mounted route for the
            // previously-orphaned domain module (C8 half A, 2026-07-18 POS
            // systemic-debugging audit). Manager+/Supervisor+ only.
            'pos.modifier-86.write',
            'pos.modifier-86.read',
            'payments.terminal.connect',
            'payments.terminal.release',
            'payments.terminal.status',
            'payments.terminal.auth-card',
            'payments.terminal.prompt-tip',
            'payments.terminal.display-message',
            'payments.terminal.cancel',
            // Workforce timeclock — shift-level approval + dispute. Edit + approve
            // mirror the void/refund pattern (Manager + Supervisor).
            'workforce.timeclock.edit',
            // Manager-initiated live clock-in/out on an employee's behalf
            // (managerPunchIn/managerPunchOut) — same Manager+Supervisor scoping as
            // workforce.timeclock.edit, per
            // docs/superpowers/specs/2026-08-02-manager-clockin-design.md.
            'workforce.timeclock.punch.others',
            'workforce.timeclock.approve',
            'workforce.timeclock.dispute',
            // Geofencing (PR C: API surface). devices.approve stays Manager+ only
            // (same "stays Manager+" pattern as pos.drawer.eod above) — approving
            // a kiosk/POS device's trust is a higher-trust action than reviewing
            // flagged punches.
            'workforce.geofencing.read',
            'workforce.geofencing.write',
            'workforce.geofencing.review',
            // Tax groups — settings.modules.tenants.read parity.
            'tax.groups.read',
            'tax.rates.read',
            'commerce.adjustment.apply',
            'commerce.service-charge.apply',
            'commerce.service-charge.waive',
            'commerce.order.add-line',
            // Tab Board (Tab Board PR-T1). Supervisor holds pos.lease.write and works tabs.
            'pos.tab-board.view',
            'pos.pickup-board.view',
            'pos.pickup-board.mark-ready',
            'pos.check.pull',
            'pos.check.view-all', // floor-oversight: see all tabs at the location
            // Receipt-printing Phase 0.
            'pos.receipts.build',
            'pos.receipts.email',
            // VT pay-link delivery PR-2 — same tier as the email sibling.
            'pos.receipts.sms',
            // Printer-dispatch substrate capstone — physical print dispatch.
            'pos.print.dispatch',
            // Printer + routing-rule reads — supervisor can read for diagnostics.
            // Writes stay Manager+ per the printer-config admin pattern.
            'pos.printers.read',
            'pos.printer-routing.read',
            // Expose the settings pages for diagnostic reads; writes stay Manager+ (#10089).
            'settings.pages.printers.read',
            'settings.pages.printers.routing.read',
            // Assistant — supervisor gets use + code.ground + read + feedback.
            'assistant.conversation.use',
            'assistant.code.ground',
            'assistant.conversation.read',
            'assistant.feedback.write',
            // Support/feedback — same 8 roles as assistant.feedback.write (D20).
            'support.feedback.submit',
            'support.feedback.read',
            'support.roadmap.view',
            'support.roadmap.vote',
            // Insights - supervisor gets query create + execute.
            'insights.query.create',
            'insights.query.execute',
            // Sales history lifecycle commands (Task 4 / PR1f-lifecycle).
            // Allows reopening a closed order for additional items.
            'commerce.order.reopen',
            // Allows marking items as exchanged on an order.
            'commerce.order.exchange',
            // Allows initiating a return (reversal order with inventory-restore posture).
            'commerce.order.return',
            // S-Boot-26 PR-6 customer-attach route (standard attach only; retroactive
            // is manager-only).
            'commerce.customer.attach',
            // S-Boot-26 PR-6 Task 7: gift-card sale at POS.
            'commerce.gift-card-sale.create',
            // Room layouts — supervisor can read + edit rooms.
            'room-layouts.rooms.read',
            'room-layouts.rooms.write',
            // Appointments (MEGA-1 S2) — per master plan §4d: bookings CRUD +
            // calendar visibility + read-only services/providers (no config authority).
            'appointments.bookings.read',
            'appointments.bookings.write',
            'appointments.calendar.read',
            'appointments.services.read',
            'appointments.providers.read',
        ],
        isOwner: false,
        isAssignable: true,
    },
    cashier: {
        id: 'cashier',
        displayName: 'Cashier',
        description: 'POS operator. Order-create + tender capture + PIN-gated refunds.',
        permissions: [
            'commerce.order.create',
            'commerce.order.read',
            'commerce.order.update',
            'payments.tender.capture',
            'payments.tender.read',
            'payments.capture.create',
            'payments.refund.requires-pin',
            'payments.vault.store',
            'payments.vault.read',
            'workforce.shift.read',
            // PR-6 F&B floor pane (#8984): read-only room list + live table data so
            // the POS floor renders for register-operating roles. Read ONLY —
            // layout editing (.write) stays manager/supervisor.
            'room-layouts.rooms.read',
            // PTO self-service (S-Boot-21b-5 PR-5): cashiers can submit requests.
            'workforce.pto.request',
            // Customers — granular vocabulary per docs/business-rules/customers-module.md.
            // Cashier collects payment-on-file at the terminal; gets view + basic
            // payment-method writes (add, set-default). verify-ach + archive stay
            // Manager+ (administrative).
            'customers.view',
            'customers.payment-methods.view',
            'customers.payment-methods.add',
            'customers.payment-methods.set-default',
            // Customer profiles V2 first-slice (sub-PR 8f); spec §4.6.
            'customers.contact.read',
            'customers.contact.write',
            'customers.stored-value.read',
            'customers.membership.read',
            'customers.activity.read',
            'customers.tags.read',
            'customers.relationships.read',
            'customers.notes.read',
            'customers.notes.write',
            'catalog.items.read',
            'catalog.modifiers.read',
            'catalog.tax.read',
            'tabs.transfer',
            'tenants.locations.read',
            // Settings spine (S-Boot-26 PR-8b cluster) — Convention 7 grants read to
            // every role except Staff. Cashier reads settings to surface defaults
            // (timezone for receipt timestamps, branding for receipt header).
            'settings.modules.tenants.read',
            // Document Templates settings (ADR-0174) -- read only, same posture as
            // the settings.modules.tenants.read grant immediately above.
            'settings.modules.document-templates.read',
            'catalog.on-hand.read',
            'gift-cards.balance.read',
            'gift-cards.validate',
            'gift-cards.issue',
            'gift-cards.redeem',
            'memberships.plan.read',
            'memberships.member.read',
            'memberships.plan.sell',
            'memberships.loyalty.redeem',
            'gift-cards.holds.manage',
            'gift-cards.view-balance-audit',
            'pos.device.read',
            'pos.check.read',
            // Virtual Terminal (VT PR-2) — card-only Quick Charge / Send-a-link
            // collection surface. Gated on this permission alone, never on
            // pos.check.read (POS isolation rule 9; I-6).
            'pos.virtual-terminal.access',
            // VT charge tabs (#14612): see the Virtual Terminal settings page (read-only).
            'settings.pages.virtual-terminal.read',
            // VT PR-4 (#8724) -- narrower-permissioned VT refund/void action,
            // distinct from the broad commerce.order.refund (D4, least-privilege).
            'pos.virtual-terminal.refund',
            'pos.check.write',
            // Custom/open-price line items get a dedicated permission, distinct
            // from the routine pos.check.write (H5, 2026-07-18 POS
            // systemic-debugging audit) — granted to the same tier as
            // pos.check.write today; a tenant can later narrow it without
            // touching other cart-write actions.
            'pos.check.custom-item',
            'pos.check.split',
            'pos.check.merge',
            'pos.lease.read',
            'pos.lease.write',
            'pos.cart.read',
            'catalog.pos.read',
            'workforce.timeclock.punch',
            // Workforce timeclock dispute — every employee can dispute their own timecard.
            'workforce.timeclock.dispute',
            // Operator-context-bootstrap permissions (S-Boot OCB PR-1).
            'pos.bootstrap.role.read',
            'pos.bootstrap.location.read',
            'pos.bootstrap.profit-center.read',
            'pos.bootstrap.terminal.read',
            'pos.session.start',
            'pos.session.end',
            'pos.terminal.resume',
            // Granular pos.drawer permissions per docs/superpowers/plans/2026-05-12-sboot-26-pr-7-shifts-drawer-eod.md.
            // Cashier opens/closes own-shift drawer; variance-review + eod stay Supervisor+.
            'pos.drawer.open',
            'pos.drawer.close',
            'payments.terminal.connect',
            'payments.terminal.release',
            'payments.terminal.status',
            'payments.terminal.auth-card',
            'payments.terminal.prompt-tip',
            'payments.terminal.display-message',
            'payments.terminal.cancel',
            // Tax groups — settings.modules.tenants.read parity.
            'tax.groups.read',
            'tax.rates.read',
            'commerce.order.add-line',
            // Tab Board (Tab Board PR-T1). Cashier holds pos.lease.write and works tabs.
            'pos.tab-board.view',
            'pos.pickup-board.view',
            'pos.pickup-board.mark-ready',
            'pos.check.pull',
            // Receipt-printing Phase 0 — cashier is the natural actor at payment time.
            'pos.receipts.build',
            'pos.receipts.email',
            // VT pay-link delivery PR-2 — the cashier is the actor who asks the guest
            // "text or email?", so withholding sms here would strand the flow.
            'pos.receipts.sms',
            // Printer-dispatch substrate capstone — physical print dispatch.
            'pos.print.dispatch',
            // Agent-pairing: the workstation (cashier terminal) pairs the native print agent.
            'pos.agent.pair',
            // Assistant — cashier gets use + code.ground + read + feedback.
            'assistant.conversation.use',
            'assistant.code.ground',
            'assistant.conversation.read',
            'assistant.feedback.write',
            // Support/feedback — same 8 roles as assistant.feedback.write (D20).
            'support.feedback.submit',
            'support.feedback.read',
            'support.roadmap.view',
            'support.roadmap.vote',
            // Sales history lifecycle commands (Task 4 / PR1f-lifecycle).
            // Allows reopening a closed order for additional items.
            'commerce.order.reopen',
            // S-Boot-26 PR-6 customer-attach route.
            'commerce.customer.attach',
            // #8617: lets the customer-attach "open deposits" nudge actually reach
            // the role that attaches customers at checkout — financials.statements.read
            // (GL-exposing, audited) is deliberately NOT granted to Cashier.
            'financials.customer-deposits.lookup',
            // S-Boot-26 PR-6 Task 7: gift-card sale at POS.
            'commerce.gift-card-sale.create',
        ],
        isOwner: false,
        isAssignable: true,
    },
    server: {
        id: 'server',
        displayName: 'Server',
        description: 'Restaurant server. Order create/update for dine-in/takeout; tab transfer; gratuity modify.',
        permissions: [
            'commerce.order.create',
            'commerce.order.read',
            'commerce.order.update',
            'payments.tender.read',
            'tabs.transfer',
            'gratuity.modify',
            'workforce.shift.read',
            // PR-6 F&B floor pane (#8984): read-only room list + live table data so
            // the POS floor renders for register-operating roles. Read ONLY —
            // layout editing (.write) stays manager/supervisor.
            'room-layouts.rooms.read',
            // PTO self-service (S-Boot-21b-5 PR-5): servers can submit requests.
            'workforce.pto.request',
            // Customers — granular vocabulary per docs/business-rules/customers-module.md.
            'customers.view',
            // Customer profiles V2 first-slice (sub-PR 8f); spec §4.6.
            'customers.contact.read',
            'customers.stored-value.read',
            'customers.membership.read',
            'customers.activity.read',
            'customers.tags.read',
            'customers.relationships.read',
            'customers.notes.read',
            'customers.notes.write',
            'catalog.items.read',
            'catalog.modifiers.read',
            'catalog.tax.read',
            'tenants.locations.read',
            // Settings spine (S-Boot-26 PR-8b cluster) — Convention 7 read to every role except Staff.
            'settings.modules.tenants.read',
            // Document Templates settings (ADR-0174) -- read only, same posture as
            // the settings.modules.tenants.read grant immediately above.
            'settings.modules.document-templates.read',
            'catalog.on-hand.read',
            'gift-cards.balance.read',
            'gift-cards.validate',
            'gift-cards.issue',
            'gift-cards.redeem',
            'memberships.plan.read',
            'memberships.member.read',
            'memberships.plan.sell',
            'memberships.loyalty.redeem',
            'gift-cards.view-balance-audit',
            'pos.device.read',
            'pos.check.read',
            // Virtual Terminal (VT PR-2) — card-only Quick Charge / Send-a-link
            // collection surface. Gated on this permission alone, never on
            // pos.check.read (POS isolation rule 9; I-6).
            'pos.virtual-terminal.access',
            // VT charge tabs (#14612): see the Virtual Terminal settings page (read-only).
            'settings.pages.virtual-terminal.read',
            // VT PR-4 (#8724) -- narrower-permissioned VT refund/void action,
            // distinct from the broad commerce.order.refund (D4, least-privilege).
            'pos.virtual-terminal.refund',
            'pos.check.write',
            // Custom/open-price line items get a dedicated permission, distinct
            // from the routine pos.check.write (H5, 2026-07-18 POS
            // systemic-debugging audit) — granted to the same tier as
            // pos.check.write today; a tenant can later narrow it without
            // touching other cart-write actions.
            'pos.check.custom-item',
            'pos.check.split',
            'pos.check.merge',
            'pos.lease.read',
            'pos.lease.write',
            'pos.cart.read',
            'catalog.pos.read',
            // Card-present terminal set (VT W6, #13830 CARDPRESENT-11). Owner ruling, verbatim: "servers
            // should by DEFAULT have access to the VT or POS they are logged into, AND custom roles must be
            // able to toggle this on or off so any permutation works." The VT card-machine rail lists
            // readers through `status` and runs the charge through `auth-card`; a server could open VT but
            // got a 403 on the reader list, so the rail's permissions are granted as a set. REAL SCOPE:
            // the POS and Clover card-reader routes check only these same permissions, so this grants
            // card-present at the POS as well as in VT, and `cancel` reaches cancel-auth, which cancels a
            // live authorization and can void it at the gateway. Deliberately NOT granted here: any capture,
            // vault or void PERMISSION (payments.tender.capture stays out). Note that VT terminal-capture is
            // guarded only by pos.virtual-terminal.access, which this role already held, so a server can now
            // authorize AND capture a VT card-present sale end to end.
            'payments.terminal.connect',
            'payments.terminal.release',
            'payments.terminal.status',
            'payments.terminal.auth-card',
            'payments.terminal.prompt-tip',
            'payments.terminal.display-message',
            'payments.terminal.cancel',
            // Server fires courses manually from POS / expo display for dine-in tickets.
            'pos.course.fire-manually',
            'workforce.timeclock.punch',
            'workforce.timeclock.dispute',
            // Operator-context-bootstrap permissions (S-Boot OCB PR-1).
            'pos.bootstrap.role.read',
            'pos.bootstrap.location.read',
            'pos.bootstrap.profit-center.read',
            'pos.bootstrap.terminal.read',
            'pos.session.start',
            'pos.session.end',
            'pos.terminal.resume',
            // Granular pos.drawer permissions per docs/superpowers/plans/2026-05-12-sboot-26-pr-7-shifts-drawer-eod.md.
            // Server opens/closes own-shift drawer.
            'pos.drawer.open',
            'pos.drawer.close',
            // Tax groups — settings.modules.tenants.read parity.
            'tax.groups.read',
            'tax.rates.read',
            'commerce.order.add-line',
            // Tab Board (Tab Board PR-T1). Server holds pos.lease.write and works tabs.
            'pos.tab-board.view',
            'pos.pickup-board.view',
            'pos.pickup-board.mark-ready',
            'pos.check.pull',
            // Assistant — server gets use + code.ground + read + feedback.
            'assistant.conversation.use',
            'assistant.code.ground',
            'assistant.conversation.read',
            'assistant.feedback.write',
            // Support/feedback — same 8 roles as assistant.feedback.write (D20).
            'support.feedback.submit',
            'support.feedback.read',
            'support.roadmap.view',
            'support.roadmap.vote',
            // #8617: lets the customer-attach "open deposits" nudge actually reach
            // the role that attaches customers at checkout — financials.statements.read
            // (GL-exposing, audited) is deliberately NOT granted to Server.
            'financials.customer-deposits.lookup',
        ],
        isOwner: false,
        isAssignable: true,
    },
    staff: {
        id: 'staff',
        displayName: 'Staff',
        description: 'Hourly worker, host, runner. Read-only baseline; cannot place orders.',
        permissions: [
            'commerce.order.read',
            // Customers — granular vocabulary per docs/business-rules/customers-module.md.
            'customers.view',
            'workforce.shift.read',
            // PTO self-service (S-Boot-21b-5 PR-5): staff can submit requests.
            'workforce.pto.request',
            'catalog.items.read',
            'catalog.modifiers.read',
            'tenants.locations.read',
            'gift-cards.view-balance-audit',
            // KDS ticket permissions per modules/kds/server/routes.ts (PR-12 Slice 5b).
            // Staff covers the kitchen-line cook tier — read + bump only. Serve is
            // expo-tier (supervisor+); recall + void are administrative.
            'kds.ticket.read',
            'kds.ticket.bump',
            // KDS extended ops (PR-12 Slice 12b.1) — cook-tier shares hold +
            // acknowledge-delta (clear modifier-change alert). Refire stays expo+.
            'kds.ticket.hold',
            'kds.ticket.acknowledge-delta',
            // KDS station-message board (S4-PR-c, 04-KDS.md §H) — same operator
            // posture as kds.ticket.hold / acknowledge-delta (no step-up, no extra
            // audit; reversible soft-dismiss). permission-metadata.ts says so.
            'kds.station-message.send',
            // KDS send-reliability ACK ingest (S4b-A2b) — same operator posture; the
            // station client posts delivery/display/heartbeat ACKs.
            'kds.order-status.ack',
            // KDS send-reliability console read (S4b-D) — Active/Needs-Attention/
            // History tabs + station health + diagnostics. Non-destructive; granted to
            // every transaction tier (manager + supervisor + staff), like kds.ticket.read.
            'kds.order-status.read',
            // Operator-context-bootstrap permissions (S-Boot OCB PR-1).
            // Staff walks the chooser flow to reach /pos for drawer-coverage shifts.
            'pos.bootstrap.role.read',
            'pos.bootstrap.location.read',
            'pos.bootstrap.profit-center.read',
            'pos.bootstrap.terminal.read',
            'pos.session.start',
            'pos.session.end',
            'pos.terminal.resume',
            // Granular pos.drawer permissions per docs/superpowers/plans/2026-05-12-sboot-26-pr-7-shifts-drawer-eod.md.
            // Staff occasionally operates a register; opens/closes drawer for cashier coverage.
            'pos.drawer.open',
            'pos.drawer.close',
            // Staff who operates a register also binds the device lease.
            'pos.lease.write',
            // MEDIUM-4 (independent security review, 2026-07-31): auto-acquire
            // (LeaseAutoAcquirer) and the lease-holder banner's "Take over"
            // button now call routes gated by pos.check.pull (open-here, claim)
            // instead of the retired direct pos.lease.write-gated acquire/
            // transfer routes (#7770). Staff was the one role holding
            // pos.lease.write without pos.check.pull — a real regression that
            // would 403 auto-acquire the moment a staff-operated register opens
            // a check. Mirrors the manager role's own comment ("all roles with
            // pos.lease.write" get pull) — deliberately NOT pos.tab-board.view /
            // pos.check.read / pos.check.view-all, which stay manager+ (see the
            // parity test just above proving staff still lacks those).
            'pos.check.pull',
            // Workforce timeclock punch — every employee clocks in/out every shift.
            'workforce.timeclock.punch',
            'workforce.timeclock.dispute',
            // Assistant — staff gets use + code.ground + read + feedback.
            'assistant.conversation.use',
            'assistant.code.ground',
            'assistant.conversation.read',
            'assistant.feedback.write',
            // Support/feedback — same 8 roles as assistant.feedback.write (D20).
            'support.feedback.submit',
            'support.feedback.read',
            'support.roadmap.view',
            'support.roadmap.vote',
            // Appointments (MEGA-1 S2) — per master plan §4d: read-only booking
            // visibility, no config or write authority.
            'appointments.bookings.read',
            'appointments.calendar.read',
        ],
        isOwner: false,
        isAssignable: true,
    },
    'accountant-view': {
        id: 'accountant-view',
        displayName: 'View only',
        description: 'External accountant with read access to financial reports and exports.',
        isOwner: false,
        isAssignable: true,
        permissions: [
            'payment-hub.settings.read',
            'payment-hub.requests.read',
            'financials.dashboard.view',
            'financials.statements.read',
            'financials.statements.export',
            // #8617: keeps parity with the pre-existing financials.statements.read
            // grant on GET /deposits/open, which now checks this narrower slug
            // instead so it can also reach POS-facing roles without widening their
            // access to full GL statements.
            'financials.customer-deposits.lookup',
            'financials.period.read',
            'financials.chart.read',
            'financials.cash-activity.read',
            'financials.gl-detail.read',
            'financials.gl-code-summary.read',
            'financials.cash-flows.read',
            // #9120 follow-up: per-location drill-down -- see the fuller rationale on
            // the manager block's copy of this grant. Required here, not optional: the
            // handler used to hardcode this permission as held, so omitting it would
            // take away drill-down this role has today.
            'financials.dimension.location.read',
            // #9120: .export siblings of the three reads above -- see the fuller
            // rationale on the manager block's copy of these same three grants.
            'financials.cash-activity.export',
            'financials.gl-detail.export',
            'financials.cash-flows.export',
            'financials.reconciliation-reports.read',
            'financials.reconciliation-reports.export',
            'financials.ar-reports.read',
            'financials.ap-reports.read',
            'financials.ap-reports.export',
            'financials.payroll-reports.read',
            'financials.payroll-reports.export',
            'financials.fixed-assets-reports.read',
            'financials.fixed-assets-reports.export',
            'financials.operational-reports.read',
            // Settlement evidence is read-only; batch mutations remain Owner-only by default.
            'payments.settlements.read',
            'financials.operational-reports.export',
            // AR Center V1: accountant-view gets aging + statement read (report-level, no mutations).
            'ar.aging.read',
            'ar.statement.list',
            'ar.statement.read',
            // Assistant — accountant-view gets use + code.ground + read + feedback.
            'assistant.conversation.use',
            'assistant.code.ground',
            'assistant.conversation.read',
            'assistant.feedback.write',
            // Support/feedback — same 8 roles as assistant.feedback.write (D20).
            'support.feedback.submit',
            'support.feedback.read',
            'support.roadmap.view',
            'support.roadmap.vote',
            // Insights - accountant-view gets query execute + registry read.
            'insights.query.execute',
            'insights.registry.read',
        ],
    },
    'accountant-journals': {
        id: 'accountant-journals',
        displayName: 'Journal access',
        description: 'External accountant who can create and post journal entries.',
        isOwner: false,
        isAssignable: true,
        permissions: [
            'payment-hub.settings.read',
            'payment-hub.requests.read',
            'financials.dashboard.view',
            'financials.statements.read',
            'financials.statements.export',
            // #8617: keeps parity with the pre-existing financials.statements.read
            // grant on GET /deposits/open, which now checks this narrower slug
            // instead so it can also reach POS-facing roles without widening their
            // access to full GL statements.
            'financials.customer-deposits.lookup',
            'financials.period.read',
            'financials.chart.read',
            'financials.cash-activity.read',
            'financials.gl-detail.read',
            'financials.gl-code-summary.read',
            'financials.cash-flows.read',
            // #9120 follow-up: per-location drill-down -- see the fuller rationale on
            // the manager block's copy of this grant. Required here, not optional: the
            // handler used to hardcode this permission as held, so omitting it would
            // take away drill-down this role has today.
            'financials.dimension.location.read',
            // #9120: .export siblings of the three reads above -- see the fuller
            // rationale on the manager block's copy of these same three grants.
            'financials.cash-activity.export',
            'financials.gl-detail.export',
            'financials.cash-flows.export',
            'financials.reconciliation-reports.read',
            'financials.reconciliation-reports.export',
            'financials.ar-reports.read',
            'financials.ap-reports.read',
            'financials.ap-reports.export',
            'financials.payroll-reports.read',
            'financials.payroll-reports.export',
            'financials.fixed-assets-reports.read',
            'financials.fixed-assets-reports.export',
            'financials.operational-reports.read',
            // Settlement evidence is read-only; batch mutations remain Owner-only by default.
            'payments.settlements.read',
            'financials.operational-reports.export',
            'financials.manual-journals.read',
            'financials.recognition.read',
            'financials.manual-journal-drafts.write',
            'financials.manual-journal-drafts.delete',
            'financials.manual-journals.approve',
            'financials.manual-journals.post',
            'financials.recognition.run',
            'financials.recognition.recover',
            'financials.gl-mapping.approve',
            // Assistant — accountant-journals gets use + code.ground + read + feedback.
            'assistant.conversation.use',
            'assistant.code.ground',
            'assistant.conversation.read',
            'assistant.feedback.write',
            // Support/feedback — same 8 roles as assistant.feedback.write (D20).
            'support.feedback.submit',
            'support.feedback.read',
            'support.roadmap.view',
            'support.roadmap.vote',
        ],
    },
    'accountant-full': {
        id: 'accountant-full',
        displayName: 'Full financial access',
        description: 'External accountant with full financial management access including period close and reconciliation.',
        isOwner: false,
        isAssignable: true,
        permissions: [
            'payment-hub.settings.read',
            'payment-hub.settings.write',
            'payment-hub.reasons.write',
            'payment-hub.custom-fields.write',
            'payment-hub.requests.read',
            'payment-hub.requests.create',
            'payment-hub.requests.revoke',
            'payment-hub.requests.retry-settlement',
            'payment-hub.requests.resend-receipt',
            'payment-hub.refund',
            'payment-hub.dispute.write',
            'payment-hub.requests.set-business-date',
            'payment-hub.requests.resolve-refund',
            'financials.dashboard.view',
            'financials.preflight.read',
            'financials.statements.read',
            'financials.statements.export',
            // #8617: keeps parity with the pre-existing financials.statements.read
            // grant on GET /deposits/open, which now checks this narrower slug
            // instead so it can also reach POS-facing roles without widening their
            // access to full GL statements.
            'financials.customer-deposits.lookup',
            'financials.period.read',
            'financials.chart.read',
            'financials.cash-activity.read',
            'financials.gl-detail.read',
            'financials.gl-code-summary.read',
            'financials.cash-flows.read',
            // #9120 follow-up: per-location drill-down -- see the fuller rationale on
            // the manager block's copy of this grant. Required here, not optional: the
            // handler used to hardcode this permission as held, so omitting it would
            // take away drill-down this role has today.
            'financials.dimension.location.read',
            // #9120: .export siblings of the three reads above -- see the fuller
            // rationale on the manager block's copy of these same three grants.
            'financials.cash-activity.export',
            'financials.gl-detail.export',
            'financials.cash-flows.export',
            'financials.reconciliation-reports.read',
            'financials.reconciliation-reports.export',
            'financials.ar-reports.read',
            'financials.ap-reports.read',
            'financials.ap-reports.export',
            'financials.payroll-reports.read',
            'financials.payroll-reports.export',
            'financials.fixed-assets-reports.read',
            'financials.fixed-assets-reports.export',
            'financials.operational-reports.read',
            // Settlement evidence is read-only; batch mutations remain Owner-only by default.
            'payments.settlements.read',
            'financials.operational-reports.export',
            'financials.manual-journals.read',
            'financials.recognition.read',
            'financials.manual-journal-drafts.write',
            'financials.manual-journal-drafts.delete',
            'financials.manual-journals.approve',
            'financials.manual-journals.post',
            'financials.recognition.run',
            'financials.recognition.recover',
            'financials.manual-journals.reverse',
            'financials.gl-mapping.approve',
            'financials.period.close',
            'financials.period.reopen',
            // Billing Deposits & Reconciliation surface (Plan D) -- full accountant
            // access mirrors manager's tier for this billing-specific surface.
            'customer-billing.reconciliation.read',
            'customer-billing.reconciliation.resolve',
            // Harden-pass fix (security review, 2026-08-31): `customer-billing.invoice.charge-card`
            // was granted here but deliberately withheld -- this role holds no
            // pay-link.create/.revoke, no pos.virtual-terminal.access, no
            // payments.tender.refund; 'External accountant' (this role's own
            // description) should not be able to initiate a card charge against
            // a customer, only reconcile/report on money already moved. manager
            // (and owner) keep the grant; this role does not.
            // Billing workspace -- accountant-full mirrors manager's read tier for
            // this billing-specific surface, same posture as reconciliation.* above.
            'customer-billing.workspace.read',
            'customer-billing.subscription.read',
            'customer-billing.plan.read',
            'customer-billing.pay-link.read',
            'financials.reconciliation.read',
            'financials.reconciliation.start',
            'financials.reconciliation.clear',
            'financials.reconciliation.finalize',
            'financials.reconciliation.adjust',
            'financials.reconciliation.amend',
            'financials.reconciliation.import',
            'financials.reconciliation.rule.manage',
            'financials.chart.create',
            'financials.chart.update',
            'financials.chart.deactivate',
            // CoA import wizard (PR4 / ADR-0077). Controller-grade role manages the
            // intelligent-import flow end-to-end: read/wizard + execute (step-up) +
            // rollback (step-up). Owner picks up via '*'.
            'financials.chart.import',
            'financials.chart.import.stage',
            'financials.chart.import.execute',
            'financials.chart.import.rollback',
            // COA PR8 governance: merge/renumber (financial_critical step-up), reactivate,
            // change-log read, recurring-template management + execution, statement layouts.
            'financials.chart.merge',
            'financials.chart.renumber',
            'financials.chart.reactivate',
            'financials.chart.change-log.read',
            'financials.recurring-templates.read',
            'financials.recurring-templates.manage',
            'financials.recurring-templates.end',
            'financials.recurring-templates.execute',
            'financials.statement-layouts.manage',
            // AP Phase 3: full accountant gets approval + config + void authority (Controller role).
            'ap.expense.read',
            'ap.expense.approve',
            'ap.bill.approve',
            'ap.bill.void',
            // AP Phase A: bill hold/release + hold-code library management.
            'ap.bill.hold',
            'ap.hold-code.read',
            'ap.hold-code.write',
            'ap.approval.config',
            // AP delegations: accountant-full can manage own delegations and view all tenant delegations.
            'ap.approval.delegate.self',
            'ap.approval.delegate.admin',
            // AP Phase 5: accountant-full gets payment batch history read (no write/execute/void).
            'ap.payment.read',
            'ap.credit.read',
            'ap.credit.write',
            'ap.credit.void',
            // AP Write Checks: accountant-full gets check read only (no write/print/void).
            'ap.check.read',
            'ap.settings.manage',
            'ap.vendor.yr1099-prep',
            // AP ACH: accountant-full gets read-only access to vendor ACH info.
            'ap.vendor.read',
            // AR Center V1: accountant-full gets AR read + post/void authority + aging + statements + dunning read.
            'ar.invoice.list',
            'ar.invoice.read',
            'ar.invoice.post',
            'ar.invoice.void',
            // Billing Quotes Phase 2: full accountants can manage and convert quotes.
            'quotes.quote.read',
            'quotes.quote.write',
            'quotes.quote.accept-decline',
            'quotes.quote.convert',
            // Write-Ups: same roles that manage the full quote lifecycle also
            // manage the PO-handoff document it produces.
            'write-ups.write-up.read',
            'write-ups.write-up.write',
            // Write-Ups settings (nav flag + Settings page) are gated separately from
            // the CRUD permission above (modules/write-ups/server/settings-routes.ts) --
            // grant read+write here so a role with full write-up access can also see
            // the nav tab and use the settings page, same posture as the tax settings
            // pairing above.
            'settings.modules.write-ups.read',
            'settings.modules.write-ups.write',
            'ar.receipt.list',
            'ar.receipt.read',
            'ar.aging.read',
            'ar.statement.list',
            'ar.statement.read',
            'ar.dunning.read',
            // OtG PR3 Task 4: accountant-full can initiate conversion entry requests (maker).
            // Approve is owner-only (step-up required).
            'financials.gl.conversion.initiate',
            // Assistant — accountant-full gets use + code.ground + read + feedback.
            'assistant.conversation.use',
            'assistant.code.ground',
            'assistant.conversation.read',
            'assistant.feedback.write',
            // Support/feedback — same 8 roles as assistant.feedback.write (D20).
            'support.feedback.submit',
            'support.feedback.read',
            'support.roadmap.view',
            'support.roadmap.vote',
            // Insights - accountant-full gets registry read + author.
            'insights.registry.read',
            'insights.registry.author',
        ],
    },
    clockin_only: {
        id: 'clockin_only',
        displayName: 'Clock-in only',
        description: 'Permission-inert shadow-account role (ADR-0134 §i). Provisioned exclusively by the ' +
            'clockin-only-workers provisioning saga — never manually granted by an admin. Grants no ' +
            'permissions; exists purely as an RBAC role-assignment target for credential-model bootstrapping.',
        permissions: [],
        isOwner: false,
        // ADR-0134 §i: the sole role excluded from any assignable-roles UI a
        // tenant admin would see. Must stay false — see isAssignable's JSDoc.
        isAssignable: false,
    },
};
/**
 * Role-power partial order. Used by the invite flow's R-105 cap check
 * ("inviter can only grant roles ≤ their own power").
 *
 * Per permissions.md §5.3.
 */
export const ROLE_POWER = {
    owner: 100,
    manager: 70,
    supervisor: 50,
    cashier: 30,
    server: 30,
    staff: 10,
    // Accountant roles are external-access roles; low power prevents invite-escalation by non-owners.
    'accountant-view': 7,
    'accountant-journals': 8,
    'accountant-full': 9,
    // Permission-inert (ADR-0134 §i): 0 means this role can never be an
    // invite-escalation target and grants zero permissions.
    clockin_only: 0,
};
/**
 * The 10 role ids as a typed tuple, useful for iteration.
 *
 * The compile-time assertion below ensures TENANT_ROLE_IDS covers the
 * full TenantRoleId union — adding a role to ROLE_REGISTRY without
 * adding it here triggers a TS2344 at the assertion line. Symmetric
 * the other way too: removing a role from ROLE_REGISTRY without
 * removing it here makes `(typeof TENANT_ROLE_IDS)[number]` no longer
 * assignable to `TenantRoleId`.
 */
export const TENANT_ROLE_IDS = [
    'owner',
    'manager',
    'supervisor',
    'cashier',
    'server',
    'staff',
    'accountant-view',
    'accountant-journals',
    'accountant-full',
    'clockin_only',
];
const _assertTenantRoleIdsExhaustive = true;
void _assertTenantRoleIdsExhaustive;
/**
 * Type guard for TenantRoleId.
 *
 * Uses Object.prototype.hasOwnProperty.call to avoid the prototype-walk
 * trap (`'constructor' in {}` returns true on a plain object). Without
 * the own-property check, a string like 'constructor' would falsely
 * narrow to TenantRoleId and break downstream type guarantees (seed
 * generator + Phase 4 matrix runner both index ROLE_REGISTRY by the
 * guarded value).
 */
export function isTenantRoleId(value) {
    return Object.prototype.hasOwnProperty.call(ROLE_REGISTRY, value);
}
//# sourceMappingURL=role-registry.js.map